// Centralized backend API client for The Loom V2.38_f55.
// All calls go through REACT_APP_BACKEND_URL (Kubernetes ingress routes /api -> :8001).
import axios from 'axios';
import { paymentsApi as authPaymentsApi } from '@/lib/auth_api';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required.');
export const API_BASE = `${BACKEND_URL}/api`;
export const API_URL  = API_BASE;   // alias used by direct <a href> asset endpoints

const http = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});
export { http };

export const retroactiveReviewApi = {
  get: (projectId) => http.get(`/admin/retroactive-review/project/${encodeURIComponent(projectId)}`).then((r) => r.data),
  queue: (projectId) => http.post(`/admin/retroactive-review/project/${encodeURIComponent(projectId)}`).then((r) => r.data),
};

export const continuityReviewApi = {
  listManuscripts: (status) => http.get('/admin/continuity-review/manuscripts', { params: { status: status || undefined } }).then((r) => r.data),
  get: (projectId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}`).then((r) => r.data),
  start: (projectId, modelId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}`, modelId ? { model_id: modelId } : {}).then((r) => r.data),
  resume: (projectId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/resume`).then((r) => r.data),
  startRework: (projectId, chapterId, modelId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/chapter/${encodeURIComponent(chapterId)}/rework`, modelId ? { model_id: modelId } : {}).then((r) => r.data),
  getRework: (projectId, chapterId, previewId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/chapter/${encodeURIComponent(chapterId)}/rework/${encodeURIComponent(previewId)}`).then((r) => r.data),
  applyRework: (projectId, chapterId, rewrittenText) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/chapter/${encodeURIComponent(chapterId)}/rework/apply`, { rewritten_text: rewrittenText }).then((r) => r.data),
  getBibleChanges: (projectId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/bible-changes`).then((r) => r.data),
  dismissBibleChange: (projectId, characterId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/bible-changes/${encodeURIComponent(characterId)}/dismiss`).then((r) => r.data),
  recheckChapter: (projectId, chapterId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/chapter/${encodeURIComponent(chapterId)}/recheck`).then((r) => r.data),
  startBulkRecheck: (projectId, characterId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/bible-changes/${encodeURIComponent(characterId)}/recheck-all`).then((r) => r.data),
  getBulkRecheck: (projectId, characterId, bulkId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/bible-changes/${encodeURIComponent(characterId)}/recheck-all/${encodeURIComponent(bulkId)}`).then((r) => r.data),
  correctDrift: (projectId) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/correct-drift`).then((r) => r.data),
  getDriftJob:  (projectId, jobId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/correct-drift/${encodeURIComponent(jobId)}`).then((r) => r.data),
  resetDrift:   (projectId) => http.delete(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/correct-drift`).then((r) => r.data),
  setAutoWalk: (projectId, enabled) => http.post(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/auto-walk`, { enabled }).then((r) => r.data),
  getAutoWalk: (projectId) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/auto-walk`).then((r) => r.data),
  getWalkHistory: (projectId, limit = 30) => http.get(`/admin/continuity-review/project/${encodeURIComponent(projectId)}/walk-history`, { params: { limit } }).then((r) => r.data),
};

export const userContinuityApi = {
  get:              (pid) => http.get(`/projects/${encodeURIComponent(pid)}/continuity-review`).then((r) => r.data),
  start:            (pid) => http.post(`/projects/${encodeURIComponent(pid)}/continuity-review/start`).then((r) => r.data),
  resume:           (pid) => http.post(`/projects/${encodeURIComponent(pid)}/continuity-review/resume`).then((r) => r.data),
  batchFix:         (pid, chapterIds) => http.post(`/projects/${encodeURIComponent(pid)}/continuity-review/batch-pacing-fix`, { chapter_ids: chapterIds }).then((r) => r.data),
  getBatchStatus:   (pid, jobId) => http.get(`/projects/${encodeURIComponent(pid)}/continuity-review/batch-fix-status/${encodeURIComponent(jobId)}`).then((r) => r.data),
};

// ── Global AI-thinking indicator ────────────────────────────────────────────
// Any request URL that matches one of these fragments is treated as an
// LLM-backed operation. The TitleBar listens for these events to show a
// universal "AI is working" badge so the user always gets feedback.
const AI_URL_PATTERNS = [
  '/generate/', '/stream/', '/synopsis', '/spell-check',
  '/continuity-review', '/plot-builder', '/world-builder',
  '/rewrite', '/arc-scores', '/extract', '/ai-', '/draft',
  '/chapters/', '/projects/',  // broad — filtered more tightly in response
];
const isAiUrl = (url = '') =>
  ['/generate', '/synopsis', '/spell-check', '/continuity', '/plot-builder',
   '/world-builder', '/rewrite', '/arc-scores', '/extract',
  ].some((p) => url.includes(p));

http.interceptors.request.use((config) => {
  if (isAiUrl(config.url)) {
    window.dispatchEvent(new CustomEvent('loom:ai-start'));
  }
  return config;
});

// iter-17 — Global paywall interceptor. The backend's `paywall.py:PaywallError`
// emits `{detail: {paywall: {...}}}` on every 402 where the user can't cover
// the cost. We surface that via a window event the React provider listens to
// so any component (including the `EditorPanel` which calls `api.generateApi`)
// gets the paywall modal without each call site re-implementing the check.
http.interceptors.response.use(
  (r) => {
    if (isAiUrl(r.config?.url)) {
      window.dispatchEvent(new CustomEvent('loom:ai-end'));
    }
    return r;
  },
  (err) => {
    if (isAiUrl(err?.config?.url)) {
      window.dispatchEvent(new CustomEvent('loom:ai-end'));
    }
    const data = err?.response?.data;
    const paywall = data && typeof data === 'object'
      ? (data.detail && typeof data.detail === 'object' && data.detail.paywall)
        || data.paywall
      : null;
    if (paywall && err?.response?.status === 402) {
      // Surface the modal via window event
      window.dispatchEvent(new CustomEvent('paywall:open', { detail: paywall }));
      // …and flatten the response so existing toast.error(e.response.data.detail)
      // call sites — which expect a string — keep working instead of trying to
      // render the nested object as JSX. The structured payload is already in
      // the dispatched event; the string detail is just the friendly fallback.
      if (data?.detail && typeof data.detail === 'object' && data.detail.detail) {
        err.response.data = { ...data, detail: data.detail.detail };
      }
    }
    // iter-26 / iter55z-c fix — Admin 401 handling: the server returns
    // different `detail` strings for the two failure modes:
    //   • "Admin token required" — the cookie NEVER arrived (usually
    //     a cross-origin SameSite issue, not an actual timeout).
    //   • "Admin session expired" / anything else — genuine expiry.
    // We ship both signals in the event so AdminPage can render an
    // honest toast instead of always blaming an idle timeout that
    // never happened.
    //
    // BUT — `/admin/impersonate-tier` is a NON-CRITICAL sidebar probe.
    // Its 401 must not bounce the whole admin console back to login,
    // because the card handles its own hidden state and re-mounting.
    // Falsely dispatching session-expired here caused a redirect loop
    // in production (see iter55z-c post-deploy report).
    if (err?.response?.status === 401 && err?.config?.url?.startsWith('/admin')) {
      const url = err.config.url || '';
      const isNonCritical = url.includes('/admin/impersonate-tier')
                           || url.includes('/admin/llm-cost/');
      if (!isNonCritical) {
        const detail = (err?.response?.data?.detail || '').toString();
        const cookieMissing = /token required/i.test(detail);
        window.dispatchEvent(new CustomEvent('admin:session-expired', {
          detail: { reason: cookieMissing ? 'cookie-missing' : 'expired',
                     serverMessage: detail },
        }));
      }
    }
    return Promise.reject(err);
  },
);

// ---------- Auth helpers (admin) ----------
// Admin auth now uses an HttpOnly cookie set by /api/admin/login. The browser
// transmits it automatically because the axios client is configured with
// `withCredentials: true`. We deliberately do NOT keep the token in
// localStorage anymore — that was an XSS exfiltration surface.
//
// We still cache the canonical username (e.g. "Administrator") in
// localStorage purely for the admin shell's display strip; that is not a
// credential and reveals nothing useful to an attacker.
const ADMIN_USER_KEY   = 'pw-admin-user';
const ADMIN_EXPIRY_KEY = 'pw-admin-expiry'; // epoch-ms of session hard expiry
export const adminAuth = {
  get user() { return localStorage.getItem(ADMIN_USER_KEY); },
  set user(v) {
    if (v) localStorage.setItem(ADMIN_USER_KEY, v);
    else localStorage.removeItem(ADMIN_USER_KEY);
  },
  /** Store the hard-expiry epoch-ms received from the login response. */
  setExpiry(expiresInSeconds) {
    if (expiresInSeconds > 0) {
      localStorage.setItem(ADMIN_EXPIRY_KEY, String(Date.now() + expiresInSeconds * 1000));
    }
  },
  /** Remaining seconds until hard expiry; null if no expiry is stored. */
  get secondsLeft() {
    const raw = localStorage.getItem(ADMIN_EXPIRY_KEY);
    if (!raw) return null;
    return Math.max(0, Math.floor((Number(raw) - Date.now()) / 1000));
  },
  clear() {
    localStorage.removeItem(ADMIN_USER_KEY);
    localStorage.removeItem(ADMIN_EXPIRY_KEY);
  },
};

// ---------- Projects ----------
export const projectsApi = {
  list:   ()       => http.get('/projects').then(r => r.data),
  // 2026-06 — Model roles: Opus on the manuscript, Sonnet in chat (overridable).
  modelRoles:    (id)       => http.get(`/projects/${id}/model-roles`).then(r => r.data),
  setModelRoles: (id, body) => http.put(`/projects/${id}/model-roles`, body).then(r => r.data),
  get:    (id)     => http.get(`/projects/${id}`).then(r => r.data),
  create: (body)   => http.post('/projects', body).then(r => r.data),
  update: (id, p)  => http.put(`/projects/${id}`, p).then(r => r.data),
  remove: (id)     => http.delete(`/projects/${id}`).then(r => r.data),
  // iter-p2-x — Multi-language Tier 1 (writing output language).
  supportedLanguages: ()       => http.get('/projects/languages/supported').then(r => r.data),
  setLanguage:        (id, code) => http.patch(`/projects/${id}/language`,
                                                 { output_language: code }).then(r => r.data),
  // Drift badge persistence — load all chapter drift results for a project
  driftBadges: (pid) => http.get(`/projects/${pid}/drift-badges`).then(r => r.data),
};

// ---------- Chapters ----------
export const chaptersApi = {
  list:    (pid)        => http.get(`/projects/${pid}/chapters`).then(r => r.data),
  create:  (pid)        => http.post(`/projects/${pid}/chapters`).then(r => r.data),
  get:     (cid)        => http.get(`/chapters/${cid}`).then(r => r.data),
  delete:  (cid)        => http.delete(`/chapters/${cid}`).then(r => r.data),
  update:  (cid, body)  => http.put(`/chapters/${cid}`, body).then(r => {
    // iter55y — Notify the wordcount hook so lifetime badges refresh.
    try { window.dispatchEvent(new CustomEvent('pw:chapter-saved', { detail: { cid } })); }
    catch { /* SSR / older browsers */ }
    return r.data;
  }),
};

// ---------- Chapter Version History ----------
export const versionsApi = {
  list:    (cid)               => http.get(`/chapters/${cid}/versions`).then(r => r.data),
  restore: (cid, vid)          => http.post(`/chapters/${cid}/versions/${vid}/restore`).then(r => r.data),
  branch:  (cid, vid)          => http.post(`/chapters/${cid}/versions/${vid}/branch`).then(r => r.data),
};

// ---------- Plagiarism (iter-14) ----------
export const plagiarismApi = {
  // Available providers + per-user wallet balances (used by the drawer
  // header to render the per-scan provider picker).
  listProviders: () => http.get('/plagiarism/providers').then(r => r.data),
  // Submit current chapter for scanning. Wallet deduction happens server-side
  // (admin sets the flat token cost in PricingPanel). `provider` is optional:
  // when set ('copyleaks' | 'copyscape' | 'turnitin') it overrides the admin
  // default so the user can pick per-scan when multiple providers exist.
  scan:        (pid, cid, opts = {}) => {
    // Back-compat — earlier callers passed a boolean for sandbox.
    const body = typeof opts === 'boolean'
      ? { sandbox: opts }
      : { sandbox: Boolean(opts.sandbox), provider: opts.provider || undefined };
    return http.post(`/projects/${pid}/chapters/${cid}/plagiarism/scan`, body).then(r => r.data);
  },
  // Get the latest scan + its open suggestions for the editor pane.
  latest:      (pid, cid) =>
    http.get(`/projects/${pid}/chapters/${cid}/plagiarism`).then(r => r.data),
  // Create an "AI rewrite" or "assign to editor" suggestion for a flagged match.
  createSuggestion: (scan_id, match_index, action, editor_user_id = null, note = '') =>
    http.post('/plagiarism/suggestions',
              { scan_id, match_index, action, editor_user_id, note }).then(r => r.data),
  // Editor (assignee or project owner) submits their edit for the flagged span.
  editorEdit:  (sid, content, note = '') =>
    http.post(`/plagiarism/suggestions/${sid}/editor-edit`, { content, note }).then(r => r.data),
  // Chapter owner approves/refuses the rewrite.
  accept:      (sid) => http.post(`/plagiarism/suggestions/${sid}/accept`).then(r => r.data),
  decline:     (sid) => http.post(`/plagiarism/suggestions/${sid}/decline`).then(r => r.data),
  inbox:       ()    => http.get('/plagiarism/inbox').then(r => r.data),
  listEditors: (pid) => http.get(`/projects/${pid}/editors`).then(r => r.data),
};

// ---------- Manuscript & Synopsis import (iter-10) ----------
export const importApi = {
  // Multipart file upload of a DOCX/PDF/TXT/MD/EPUB manuscript.
  manuscript: (file, meta = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (meta.name)              fd.append('name', meta.name);
    if (meta.author)            fd.append('author', meta.author);
    if (meta.genre)             fd.append('genre', meta.genre);
    if (meta.words_per_chapter) fd.append('words_per_chapter', String(meta.words_per_chapter));
    // iter55f86 — Respect the "Chapter names Off" toggle so the
    // importer stores `Chapter N` labels instead of parser guesses.
    if (meta.chapter_titles_enabled === false) {
      fd.append('chapter_titles_enabled', 'false');
    }
    return http.post('/projects/import/manuscript', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  // JSON synopsis → new project + chapter outlines (+ optional auto-draft).
  synopsis: (body) => http.post('/projects/import/synopsis', body).then(r => r.data),
};

// ---------- Character & Variable exports (iter-10) ----------
export const exportExtrasApi = {
  // Returns the download URL string (NOT a Blob) so the caller can use a
  // standard `<a download>` for a clean browser file-save dialog.
  charactersUrl: (pid, format) =>
    `${API_BASE}/projects/${pid}/export/characters?format=${encodeURIComponent(format)}`,
  variablesUrl:  (pid, format, chapterId) => {
    const cid = chapterId ? `&chapter_id=${encodeURIComponent(chapterId)}` : '';
    return `${API_BASE}/projects/${pid}/export/variables?format=${encodeURIComponent(format)}${cid}`;
  },
  // iter-53l — Manuscript download URL for the Save-As-to-device path.
  // Re-uses the existing /export/novel endpoint that already produces
  // docx / txt / pdf via routes_exports.py.
  manuscriptUrl: (pid, format) =>
    `${API_BASE}/projects/${pid}/export/novel?format=${encodeURIComponent(format)}`,
};

// ---------- Characters ----------
export const charactersApi = {
  list:             (pid)             => http.get(`/projects/${pid}/characters`).then(r => r.data),
  create:           (pid, body)       => http.post(`/projects/${pid}/characters`, body).then(r => r.data),
  update:           (cid, body)       => http.put(`/characters/${cid}`, body).then(r => r.data),
  remove:           (cid)             => http.delete(`/characters/${cid}`).then(r => r.data),
  suggestAge:       (cid)             => http.post(`/characters/${cid}/suggest-age`, {}, { timeout: 30000 }).then(r => r.data),
  suggestArchetype: (cid, archetypes) => http.post(`/characters/${cid}/suggest-archetype`, { archetypes }, { timeout: 45000 }).then(r => r.data),
  seriesCarryover:  (cid, val)        => http.patch(`/characters/${cid}/series-carryover`, { series_carryover: val }).then(r => r.data),
};

// iter55g7 — Community Character Codex.
export const codexApi = {
  publish:   (body)        => http.post('/codex/publish',   body).then(r => r.data),
  unpublish: (character_id) => http.post('/codex/unpublish', { character_id }).then(r => r.data),
  mine:      ()            => http.get('/codex/mine').then(r => r.data),
  list:      (params = {}) => http.get('/codex/entries', { params }).then(r => r.data),
  detail:    (eid)         => http.get(`/codex/entries/${eid}`).then(r => r.data),
  importInto:(eid, body)   => http.post(`/codex/entries/${eid}/import`, body).then(r => r.data),
};

// iter55g10 — Novel Framework "Draft a logline for me" starter.
export const frameworkApi = {
  draftLoglines: (body = {}) =>
    http.post('/framework/draft-loglines', body, { timeout: 45000 }).then(r => r.data),
  get: (fid) => http.get(`/framework/${fid}`).then(r => r.data),   // iter55g21
  create: (body) => http.post('/framework', body).then(r => r.data), // iter55g22
  patch:  (fid, body) => http.patch(`/framework/${fid}`, body).then(r => r.data),
  // Feb-2026 · push a "Generate Synopsis" result into a Framework doc
  // so it's editable/fleshable-out and reusable for future stories —
  // omit framework_id to create a fresh one, pass it to merge instead.
  saveSynopsis: (body) => http.post('/framework/save-synopsis', body, { timeout: 20000 }).then(r => r.data),
};

// iter55g12 — Convert Story to Theatrical Script.
export const scriptsApi = {
  // Mar-2026 — background-job pattern: start returns {job_id,status} immediately;
  // caller polls jobStatus() until status=='done', then fetches via get(script_id).
  convert:   (pid, body)      => http.post(`/projects/${pid}/scripts/convert`, body).then(r => r.data),
  jobStatus: (pid, jobId)     => http.get(`/projects/${pid}/scripts/jobs/${jobId}`).then(r => r.data),
  latestJob: (pid)             => http.get(`/projects/${pid}/scripts/jobs/latest`).then(r => r.data),
  list:     (pid)              => http.get(`/projects/${pid}/scripts`).then(r => r.data),
  get:      (sid)              => http.get(`/scripts/${sid}`).then(r => r.data),
  remove:   (sid)              => http.delete(`/scripts/${sid}`).then(r => r.data),
  exportUrl:(sid, fmt)         => `${API_BASE}/scripts/${sid}/export?format=${encodeURIComponent(fmt)}`,
  // iter55g13 · Phase 2+3 — edit, revisions, cast import.
  meta:        ()               => http.get('/scripts/meta').then(r => r.data),
  update:      (sid, body)      => http.put(`/scripts/${sid}`, body, { timeout: 30000 }).then(r => r.data),
  importCast:  (sid, body = {}) => http.post(`/scripts/${sid}/characters/import`, body).then(r => r.data),
  revisions:   (sid)            => http.get(`/scripts/${sid}/revisions`).then(r => r.data),
  revision:    (sid, rid)       => http.get(`/scripts/${sid}/revisions/${rid}`).then(r => r.data),
  diff:        (sid, from_, to_) => http.get(`/scripts/${sid}/diff`,
                                             { params: { from: from_, to: to_ } }).then(r => r.data),
};

// iter55g18 — Bulk novel-build & manuscript analyzer.
export const novelBuildApi = {
  start:     (pid, body)   => http.post(`/projects/${pid}/build/start`, body, { timeout: 180000 }).then(r => r.data),
  status:    (pid)         => http.get(`/projects/${pid}/build/status`).then(r => r.data),
  get:       (jobId)       => http.get(`/build/${jobId}`).then(r => r.data),
  cancel:    (jobId)       => http.post(`/build/${jobId}/cancel`).then(r => r.data),
  resume:    (jobId)       => http.post(`/build/${jobId}/resume`).then(r => r.data),
  analyze:   (pid, body = {}) => http.post(`/projects/${pid}/analyze-manuscript`, body, { timeout: 120000 }).then(r => r.data),
  frameworkFromManuscript: (fid) => http.post(`/framework/${fid}/generate-from-manuscript`, {}, { timeout: 120000 }).then(r => r.data),
  flowPass:  (pid, cid, notesOnly = false) =>
    http.post(`/projects/${pid}/chapters/${cid}/flow-pass`, { notes_only: notesOnly }, { timeout: 180000 }).then(r => r.data),
};

// iter55g50 — Author Business Dashboard + Marketing Marketplace.
export const businessApi = {
  dashboard:     ()               => http.get('/business/dashboard').then(r => r.data),
  catalog:       ()               => http.get('/business/marketing/catalog').then(r => r.data),
  optIn:         (platform_id, opts = {}) =>
    http.post('/business/marketing/opt-in',
              { platform_id, ...opts }).then(r => r.data),
  updateOptIn:   (opt_in_id, patch) =>
    http.patch(`/business/marketing/opt-in/${opt_in_id}`, patch).then(r => r.data),
  bookService:   (platform_id, opts = {}) =>
    http.post('/business/marketing/book-service',
              { platform_id, ...opts }).then(r => r.data),
  bookings:      ()               => http.get('/business/marketing/bookings').then(r => r.data),
  bookingReply:  (booking_id, text) =>
    http.post(`/business/marketing/bookings/${booking_id}/reply`, { text }).then(r => r.data),
  bookingMarkSeen: (booking_id) =>
    http.post(`/business/marketing/bookings/${booking_id}/seen`).then(r => r.data),
  unreadCount:   ()               => http.get('/business/marketing/unread-count').then(r => r.data),
  voicePrefsGet: ()               => http.get('/business/voice-prefs').then(r => r.data),
  voicePrefsPut: (key, index, text = '') =>
    http.put('/business/voice-prefs', { key, entry: { index, text } }).then(r => r.data),
  voicePrefsHistory: (key) =>
    http.get(`/business/voice-prefs/history?key=${encodeURIComponent(key)}`).then(r => r.data),
  emailPrefsGet: ()               => http.get('/business/email-prefs').then(r => r.data),
  emailPrefsSet: (email_ops_notes) =>
    http.patch('/business/email-prefs', { email_ops_notes }).then(r => r.data),
  voicePreview:  (body)           =>
    http.post('/business/template/voice-preview', body, { timeout: 45000 }).then(r => r.data),
};

export const businessCenterApi = {
  get:               (projectId) => http.get('/business/center', { params: projectId ? { project_id: projectId } : {} }).then(r => r.data),
  setDefaultModel:   (model_id) => http.patch('/business/center/default-model', { model_id }).then(r => r.data),
  setProjectModel:   (projectId, body) => http.patch(`/business/center/projects/${projectId}/model`, body).then(r => r.data),
  setAnalysis:       (enabled) => http.patch('/business/center/marketing/analysis', { enabled }).then(r => r.data),
  addMedia:          (body) => http.post('/business/center/media', body).then(r => r.data),
  updateMedia:       (id, body) => http.patch(`/business/center/media/${id}`, body).then(r => r.data),
  deleteMedia:       (id) => http.delete(`/business/center/media/${id}`).then(r => r.data),
  generateCaptions:  (id) => http.post(`/business/center/media/${id}/captions/generate`).then(r => r.data),
  updateCaptions:    (id, vtt) => http.put(`/business/center/media/${id}/captions`, { vtt }).then(r => r.data),
  setRecoveryEmail:  (recovery_email) => http.post('/business/center/recovery-email', { recovery_email }).then(r => r.data),
  verifyRecovery:    (token) => http.get('/business/center/recovery-email/verify', { params: { token } }).then(r => r.data),
};

export const mediaVaultApi = {
  health: () => http.get('/admin/media-vault/health').then((r) => r.data),
  list: () => http.get('/admin/media-vault/media').then((r) => r.data),
  detail: (id) => http.get(`/admin/media-vault/media/${encodeURIComponent(id)}`).then((r) => r.data),
  mirror: (body) => http.post('/admin/media-vault/media/mirror', body, { timeout: 300000 }).then((r) => r.data),
  upload: (file, meta, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([key, value]) => form.append(key, value ?? ''));
    return http.post('/admin/media-vault/media/upload', form, {
      timeout: 310000,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => onProgress?.(event.total ? Math.round((event.loaded / event.total) * 100) : 0),
    }).then((r) => r.data);
  },
  remove: (id) => http.delete(`/admin/media-vault/media/${encodeURIComponent(id)}`).then((r) => r.data),
  entities: () => http.get('/admin/media-vault/entities').then((r) => r.data),
  attachments: (params = {}) => http.get('/admin/media-vault/attachments', { params }).then((r) => r.data),
  attach: (body) => http.post('/admin/media-vault/attachments', body).then((r) => r.data),
  detach: (id) => http.delete(`/admin/media-vault/attachments/${encodeURIComponent(id)}`).then((r) => r.data),
  migrationInventory: () => http.get('/admin/media-vault/migrations/inventory').then((r) => r.data),
  migrationJobs: () => http.get('/admin/media-vault/migrations/jobs').then((r) => r.data),
  migrationPerformance: () => http.get('/admin/media-vault/performance').then((r) => r.data),
  childContract: () => http.get('/admin/media-vault/child-contract').then((r) => r.data),
  startMigration: (candidateIds) => http.post('/admin/media-vault/migrations/jobs', { candidate_ids: candidateIds }).then((r) => r.data),
  retryMigration: (jobId) => http.post(`/admin/media-vault/migrations/jobs/${encodeURIComponent(jobId)}/retry`).then((r) => r.data),
  previewUrl: (id) => `${API_BASE}/admin/media-vault/media/${encodeURIComponent(id)}/download?inline=true`,
  downloadUrl: (id) => `${API_BASE}/admin/media-vault/media/${encodeURIComponent(id)}/download`,
  attachmentUrl: (id) => `${API_BASE}/media-assets/${encodeURIComponent(id)}`,
};

// iter55g50 — Admin marketing platform catalog CRUD.
export const adminBookstoreInsightsApi = {
  readerLanguages: () => http.get('/admin/bookstore/reader-languages/stats').then(r => r.data),
  booksByLanguage: () => http.get('/admin/bookstore/reader-languages/books-by-language').then(r => r.data),
  // iter-p3-i — Translation commission queue.
  listTasks:            (status = 'open') =>
    http.get('/admin/bookstore/translation-tasks', { params: { status } }).then(r => r.data),
  commissionTranslation: (body) =>
    http.post('/admin/bookstore/translation-tasks', body).then(r => r.data),
  updateTask: (tid, status) =>
    http.patch(`/admin/bookstore/translation-tasks/${tid}`, { status }).then(r => r.data),
  // iter-p3-k — Vendor email admin config (legacy single-value).
  getVendorEmail: () =>
    http.get('/admin/bookstore/translation-vendor-email').then(r => r.data),
  setVendorEmail: (email) =>
    http.patch('/admin/bookstore/translation-vendor-email', { email }).then(r => r.data),
  // iter-p3-l — Vendor routing table (per-language agency).
  getVendorRoutes: () =>
    http.get('/admin/bookstore/translation-vendor-routes').then(r => r.data),
  upsertVendorRoute: (language, email) =>
    http.put('/admin/bookstore/translation-vendor-routes', { language, email }).then(r => r.data),
  deleteVendorRoute: (language) =>
    http.delete(`/admin/bookstore/translation-vendor-routes/${language}`).then(r => r.data),
  // iter-p3-l — Wishlist admin (pending demand + one-click notify).
  pendingWishlists: () =>
    http.get('/admin/bookstore/wishlist/pending').then(r => r.data),
  notifyWishlist: (book_id) =>
    http.post(`/admin/bookstore/wishlist/notify/${book_id}`).then(r => r.data),
};

export const adminMarketingApi = {
  list:     ()          => http.get('/admin/marketing-platforms').then(r => r.data),
  upsert:   (body)      => http.post('/admin/marketing-platforms', body).then(r => r.data),
  remove:   (id)        => http.delete(`/admin/marketing-platforms/${id}`).then(r => r.data),
  // iter55g52 — Admin Booking Console.
  bookings: (status)    => http.get('/admin/marketing-bookings',
                                     status ? { params: { status } } : undefined).then(r => r.data),
  patchBooking: (id, patch) =>
    http.patch(`/admin/marketing-bookings/${id}`, patch).then(r => r.data),
  // iter55h — Ops reply thread.
  replyBooking: (id, text) =>
    http.post(`/admin/marketing-bookings/${id}/reply`, { text }).then(r => r.data),
  teammates: () => http.get('/admin/teammates').then(r => r.data),
};

// iter-p2-w / iter-p2-y — Downgrade-retention email cron admin API.
export const adminRetentionApi = {
  stats:    (sinceDays = 90) => http.get('/admin/emails/downgrade-retention/stats',
                                            { params: { since_days: sinceDays } }).then(r => r.data),
  runNow:   ()               => http.post('/admin/emails/downgrade-retention/run-now').then(r => r.data),
};

// iter-p2-z — Admin email translator (translate a support reply into
// any supported language before sending it back to a customer).
export const adminEmailTranslatorApi = {
  languages: ()  => http.get('/admin/emails/translator/languages').then(r => r.data),
  translate: (body) => http.post('/admin/emails/translator/translate', body,
                                    { timeout: 60000 }).then(r => r.data),
};

// iter-p3-b — Admin folder-based email inbox (Cloudflare Email
// Routing → Worker → /api/webhooks/email-inbound → threads).
export const adminInboxApi = {
  stats:     ()                            => http.get('/admin/inbox/stats').then(r => r.data),
  threads:   (folder = 'all', status = 'open', limit = 100, account = '') =>
               http.get('/admin/inbox/threads',
                          { params: { folder, status, limit, ...(account ? { account } : {}) } }).then(r => r.data),
  thread:    (tid)                         => http.get(`/admin/inbox/threads/${tid}`).then(r => r.data),
  accounts:  ()                            => http.get('/admin/inbox/accounts').then(r => r.data),
  compose:   (body)                        => http.post('/admin/inbox/compose', body, { timeout: 30000 }).then(r => r.data),
  setFolder: (tid, folder)                 => http.post(`/admin/inbox/threads/${tid}/folder`,
                                                          { folder }).then(r => r.data),
  assign:    (tid, assignee)               => http.post(`/admin/inbox/threads/${tid}/assign`,
                                                          { assignee }).then(r => r.data),
  markRead:  (tid)                         => http.post(`/admin/inbox/threads/${tid}/read`).then(r => r.data),
  reply:     (tid, body)                   => http.post(`/admin/inbox/threads/${tid}/reply`, body,
                                                          { timeout: 30000 }).then(r => r.data),
  aliasFor:  (userId)                      => http.get(`/admin/inbox/aliases/${userId}`).then(r => r.data),
  // Feb-2026 iter — archive / delete / bulk-cleanup.
  archive:   (tid)                         => http.post(`/admin/inbox/threads/${tid}/archive`).then(r => r.data),
  unarchive: (tid)                         => http.post(`/admin/inbox/threads/${tid}/unarchive`).then(r => r.data),
  remove:    (tid)                         => http.delete(`/admin/inbox/threads/${tid}`).then(r => r.data),
  bulkArchive: (thread_ids)                => http.post('/admin/inbox/threads/bulk-archive',
                                                          { thread_ids }).then(r => r.data),
  bulkDelete:  (thread_ids)                => http.post('/admin/inbox/threads/bulk-delete',
                                                          { thread_ids }).then(r => r.data),
  // iter-auto-assign — recipient → teammate auto-tagging rules.
  getAutoAssign: ()                        => http.get('/admin/inbox/auto-assign').then(r => r.data),
  setAutoAssign: (mapping)                 => http.put('/admin/inbox/auto-assign',
                                                          { mapping }).then(r => r.data),
};

// iter-p3-c — User-scoped inbox surface inside The Loom (authors get
// their own mailbox — the admin folder view mirrored at user scope).
export const inboxApi = {
  myAlias:   ()                            => http.get('/inbox/my-alias').then(r => r.data),
  threads:   (folder = 'all', status = 'open', limit = 100) =>
               http.get('/inbox/my',
                          { params: { folder, status, limit } }).then(r => r.data),
  thread:    (tid)                         => http.get(`/inbox/my/threads/${tid}`).then(r => r.data),
  markRead:  (tid)                         => http.post(`/inbox/my/threads/${tid}/read`).then(r => r.data),
  reply:     (tid, body)                   => http.post(`/inbox/my/threads/${tid}/reply`, body,
                                                          { timeout: 30000 }).then(r => r.data),
  // iter-p3-f — Start a brand-new thread from scratch.
  compose:   (body)                        => http.post('/inbox/my/compose', body,
                                                          { timeout: 30000 }).then(r => r.data),
};

// ---------- Variables / mode / toggles ----------
export const variablesApi = {
  get:       (pid)        => http.get(`/projects/${pid}/variables`).then(r => r.data),
  update:    (pid, body)  => http.put(`/projects/${pid}/variables`, body).then(r => r.data),
  inference: (pid)        => http.post(`/projects/${pid}/variables/inference`, {}, { timeout: 90000 }).then(r => r.data),
};

// ---------- Generation ----------
// Background-job polling helper — 3 s intervals, 6-min ceiling.
// Fires until the job reports 'done' or 'failed', then resolves/rejects.
const _pollAnalysisJob = async (statusUrl, intervalMs = 3000, maxMs = 360_000) => {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, intervalMs));
    const { data } = await http.get(statusUrl);
    if (data.status === 'done')   return data.result;
    if (data.status === 'failed') throw new Error(data.error || 'Analysis failed');
    // status === 'running' → keep polling
  }
  throw new Error('Analysis timed out after 6 minutes');
};

export const generateApi = {
  chapter:  (body) => http.post('/generate/chapter',  body, { timeout: 180000 }).then(r => r.data),
  outline:  (body) => http.post('/generate/outline',  body, { timeout: 180000 }).then(r => r.data),
  cont:     (body) => http.post('/generate/continue', body, { timeout: 120000 }).then(r => r.data),
  revise:   (body) => http.post('/generate/revise',   body, { timeout: 120000 }).then(r => r.data),
  toneShift:(body) => http.post('/generate/tone-shift', body, { timeout: 120000 }).then(r => r.data),
  build:    (body) => http.post('/build-novel',       body, { timeout: 900000 }).then(r => r.data),
  // Analysis endpoints now use background jobs to avoid the 120 s proxy
  // read-timeout. The transparent polling wrapper means all callers
  // (AnalysisPanel, ToolsMenu, EditorPanel) continue to await a result
  // with no code changes on their side.
  analyzeChapter: async (cid) => {
    const { data: start } = await http.post(`/analyze/chapter/${cid}`, {});
    return _pollAnalysisJob(`/analyze/chapter/${cid}/job/${start.job_id}`);
  },
  analyzeNovel: async (pid) => {
    const { data: start } = await http.post(`/analyze/novel/${pid}`, {});
    return _pollAnalysisJob(`/analyze/novel/${pid}/job/${start.job_id}`);
  },
  getAnalysis:      (_pid, cid) => http.get(`/analyze/chapter/${cid}`).then(r => r.data).catch(() => null),
  getCachedChapter: (cid)       => http.get(`/analyze/chapter/${cid}`).then(r => r.data),
  getCachedNovel:   (pid)       => http.get(`/analyze/novel/${pid}`).then(r => r.data),
};

// ---------- Word Count Adjustment ----------
export const wordCountApi = {
  /** Save a per-chapter target word count. */
  setChapterTarget: (chapterId, targetWords) =>
    http.patch(`/chapters/${chapterId}/word-count-target`, { target_words: targetWords }).then(r => r.data),
  /** Ask Claude to expand a chapter to the target word count (Indie Author+ gated). */
  expandChapter: (projectId, chapterId, targetWords, instruction = null) =>
    http.post(
      `/projects/${projectId}/chapters/${chapterId}/expand`,
      { target_words: targetWords, ...(instruction ? { instruction } : {}) },
      { timeout: 180000 },
    ).then(r => r.data),
};

// ---------- Issue Repair ----------
export const issueRepairApi = {
  /** Start a per-chapter Claude repair job (one or more issues from a single chapter). */
  startRepair: (projectId, issues) =>
    http.post(`/projects/${projectId}/issue-repair`, { issues }).then(r => r.data),

  /** Start a bulk repair job — every issue in the issues array, across the whole manuscript. */
  startBulkRepair: (projectId, issues) =>
    http.post(`/projects/${projectId}/issue-repair/bulk`, { issues }).then(r => r.data),

  /** Poll a job until done/failed (max ~3 min). */
  pollJob: async (projectId, jobId) => {
    const MAX = 36; // 36 × 5 s = 3 min
    for (let i = 0; i < MAX; i++) {
      await new Promise(res => setTimeout(res, 5000));
      const data = await http.get(`/projects/${projectId}/issue-repair/jobs/${jobId}`).then(r => r.data);
      if (data.job?.status === 'done' || data.job?.status === 'failed') return data;
    }
    throw new Error('Repair job timed out');
  },

  /** Writer manually edits the chapter text and saves. */
  saveManual: (repairId, content) =>
    http.post(`/issue-repair/repairs/${repairId}/save-manual`, { content }).then(r => r.data),

  /** Accept a Claude-generated fix — writes patched_content to the chapter. */
  acceptRepair: (repairId) =>
    http.post(`/issue-repair/repairs/${repairId}/accept`).then(r => r.data),

  /** Skip (dismiss) a repair without changes. */
  skipRepair: (repairId) =>
    http.post(`/issue-repair/repairs/${repairId}/skip`).then(r => r.data),
};

// ---------- Chat ----------
export const chatApi = {
  list:  (pid)       => http.get(`/projects/${pid}/chat`).then(r => r.data),
  send:  (pid, text) => http.post(`/projects/${pid}/chat`, { text }, { timeout: 120000 }).then(r => r.data),
  clear: (pid)       => http.delete(`/projects/${pid}/chat`).then(r => r.data),
  model: (pid)       => http.get(`/projects/${pid}/chat/model`).then(r => r.data),
  setModel: (pid, modelId) => http.put(`/projects/${pid}/chat/model`, { model_id: modelId }).then(r => r.data),
};

// ---------- Usage / token budget (iter-53n++) ----------
export const usageApi = {
  me: () => http.get('/usage/me').then(r => r.data),
};

// ---------- 2026-08 — Pro BYOK: user-level LLM key storage ----------
export const userLlmKeysApi = {
  list:    () => http.get('/user-llm-keys').then(r => r.data),
  set:     (provider, apiKey) => http.put(`/user-llm-keys/${provider}`, { api_key: apiKey }).then(r => r.data),
  clear:   (provider) => http.delete(`/user-llm-keys/${provider}`).then(r => r.data),
  savings: () => http.get('/user-llm-keys/savings').then(r => r.data),
};

// ---------- Templates (iter-53n++ pass 4) ----------
export const templatesApi = {
  list:      () => http.get('/me/templates').then(r => r.data),
  fromTemplate: (tid, title) => http.post(`/projects/from-template/${tid}`,
                                          title ? { title } : {},
                                          { timeout: 30000 }).then(r => r.data),
  // iter-53n++ pass 5 — Library management
  outline:   (tid) => http.get(`/me/templates/${tid}/outline`).then(r => r.data),
  rename:    (tid, name) => http.put(`/projects/${tid}`, { name }).then(r => r.data),
  remove:    (tid) => http.delete(`/projects/${tid}`).then(r => r.data),
};

// ---------- Marketplace (iter-53n++ pass 6) ----------
export const marketplaceApi = {
  publish:    (tid, tier) => http.post(`/marketplace/templates/${tid}/publish`,
                                       tier ? { tier } : {}).then(r => r.data),
  unpublish:  (tid) => http.post(`/marketplace/templates/${tid}/unpublish`, {}).then(r => r.data),
  browse:     (params = {}) => http.get('/marketplace/templates', { params }).then(r => r.data),
  detail:     (tid) => http.get(`/marketplace/templates/${tid}`).then(r => r.data),
  // iter-53n++ pass 8 — real Stripe checkout. Returns
  // {checkout_url, session_id}; frontend redirects the buyer to Stripe.
  purchase:   (tid, opts = {}) => http.post(`/marketplace/templates/${tid}/purchase`,
                                        {
                                          ...(opts.title ? { title: opts.title } : {}),
                                          origin_url: opts.origin_url || window.location.origin,
                                          idempotency_key: opts.idempotency_key
                                            || (crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`),
                                        },
                                        { timeout: 30000 }).then(r => r.data),
  checkoutStatus: (sessionId) => http.get(`/marketplace/checkout/status/${sessionId}`,
                                        { timeout: 15000 }).then(r => r.data),
  report:     (tid, { reason, details = '' }) => http.post(
                                        `/marketplace/templates/${tid}/report`,
                                        { reason, details }).then(r => r.data),
  // Admin surface — moderation queue.
  adminListReports: (statusFilter = 'open') => http.get('/admin/marketplace/reports',
                                        { params: { status: statusFilter } }).then(r => r.data),
  adminReportAction: (rid, action, note = '') => http.post(
                                        `/admin/marketplace/reports/${rid}/action`,
                                        { action, note }).then(r => r.data),
};

// ---------- Exports (browser download) ----------
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
}

function filenameFromResponse(res, fallback) {
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^"]+?)"?(?:;|$)/i);
  return m ? decodeURIComponent(m[1]) : fallback;
}

async function downloadFile(url, fallback) {
  const res = await http.get(url, { responseType: 'blob', timeout: 180000 });
  triggerDownload(res.data, filenameFromResponse(res, fallback));
}

export const exportApi = {
  novel:   (pid, fmt)      => downloadFile(`/projects/${pid}/export/novel?format=${fmt}`,   `novel.${fmt}`),
  chapter: (pid, cid, fmt) => downloadFile(`/projects/${pid}/export/chapter/${cid}?format=${fmt}`, `chapter.${fmt}`),
  chat:    (pid, fmt)      => downloadFile(`/projects/${pid}/export/chat?format=${fmt}`,    `conversation.${fmt}`),
  // iter55z-n — Adobe PDF Services print-ready book PDF (KDP/IngramSpark
  // trim sizes). Uses POST since the Adobe workflow is quota-charged
  // to our org and we don't want browser prefetch to trigger it.
  printReadyBook: async (pid, page_size = 'US_LETTER') => {
    const res = await http.post(`/exports/${pid}/book-pdf?page_size=${encodeURIComponent(page_size)}`,
      null, { responseType: 'blob', timeout: 180000 });
    triggerDownload(res.data, filenameFromResponse(res, `${pid}-print-ready.pdf`));
  },
  pdfServicesStatus: () => http.get('/exports/pdf-services/status').then(r => r.data),

  // iter55f80 — Background PDF export jobs. Enqueue Adobe render as a
  // job and poll for completion (or wait for the completion email).
  queueBookPdf: (pid, page_size = 'US_LETTER') =>
    http.post(`/exports/${pid}/book-pdf?page_size=${encodeURIComponent(page_size)}&async=true`)
        .then(r => r.data),
  queueKdpBundle: (pid, page_size = 'US_TRADE_6X9') =>
    http.post(`/exports/${pid}/kdp-bundle?page_size=${encodeURIComponent(page_size)}&async=true`)
        .then(r => r.data),
  listPdfJobs:  (limit = 20) => http.get(`/exports/pdf-jobs?limit=${limit}`).then(r => r.data),
  getPdfJob:    (jid)       => http.get(`/exports/pdf-jobs/${jid}`).then(r => r.data),
  deletePdfJob: (jid)       => http.delete(`/exports/pdf-jobs/${jid}`).then(r => r.data),
  downloadPdfJob: async (jid, fallback = 'export.pdf') => {
    const res = await http.get(`/exports/pdf-jobs/${jid}/download`,
      { responseType: 'blob', timeout: 180000 });
    triggerDownload(res.data, filenameFromResponse(res, fallback));
  },
};

// ---------- Settings ----------
export const settingsApi = {
  get: () => http.get('/settings').then(r => r.data),
};

// ---------- User caching prefs ----------
export const cachingPrefsApi = {
  get:    ()     => http.get('/user/caching-prefs').then(r => r.data),
  update: (body) => http.put('/user/caching-prefs', body).then(r => r.data),
};

// ---------- Models catalog ----------
export const modelsApi = {
  list: () => http.get('/models').then(r => r.data),
  adminList: () => http.get('/admin/models').then(r => r.data),
};

// ---------- Documents (iter55z-e) — extract plain text from .docx/.pdf/.txt/.md/.rtf ----------
export const documentsApi = {
  extractText: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await http.post('/documents/extract-text', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};

// ---------- Budget ----------
export const budgetApi = {
  project: (pid) => http.get(`/projects/${pid}/budget`).then(r => r.data),
};

// ---------- Edit Walk (admin-only manuscript walker) ----------
export const editWalkApi = {
  start:         (pid, body) => http.post(`/edit-walk/projects/${pid}/start`, body || {}).then(r => r.data),
  latest:        (pid)       => http.get(`/edit-walk/projects/${pid}/latest`).then(r => r.data),
  history:       (pid, params) => http.get(`/edit-walk/projects/${pid}/history`, { params }).then(r => r.data),
  job:           (jobId)     => http.get(`/edit-walk/jobs/${jobId}`).then(r => r.data),
  cancel:        (jobId)     => http.post(`/edit-walk/jobs/${jobId}/cancel`).then(r => r.data),
  resume:        (jobId)     => http.post(`/edit-walk/jobs/${jobId}/resume`).then(r => r.data),
  results:       (jobId)     => http.get(`/edit-walk/jobs/${jobId}/results`).then(r => r.data),
  resultDetail:  (resultId)  => http.get(`/edit-walk/results/${resultId}`).then(r => r.data),
  accept:        (resultId)  => http.post(`/edit-walk/results/${resultId}/accept`).then(r => r.data),
  reject:        (resultId)  => http.post(`/edit-walk/results/${resultId}/reject`).then(r => r.data),
  models:        ()          => http.get('/edit-walk/models').then(r => r.data),
  applyAll:      (jobId)     => http.post(`/edit-walk/jobs/${jobId}/apply-all`).then(r => r.data),
};

// ── Plot Extraction — structured plot builder + sales readiness scorer ──────
export const plotExtractionApi = {
  extract:         (pid, structureType = '3-act') =>
    http.post(`/projects/${pid}/plot-extraction?structure_type=${encodeURIComponent(structureType)}`, {}, { timeout: 120000 }).then(r => r.data),
  get:             (pid)        => http.get(`/projects/${pid}/plot-extraction`).then(r => r.data),
  update:          (pid, patch) => http.put(`/projects/${pid}/plot-extraction`, patch).then(r => r.data),
  salesReadiness:  (pid)        => http.get(`/projects/${pid}/plot-extraction/sales-readiness`).then(r => r.data),
  strengthen:      (pid, structureType) =>
    http.post(`/projects/${pid}/plot-extraction/strengthen`, { structure_type: structureType }, { timeout: 90000 }).then(r => r.data),
  buildSynopsis:   (pid, structureType) =>
    http.post(`/projects/${pid}/plot-extraction/synopsis`, { structure_type: structureType }, { timeout: 90000 }).then(r => r.data),
  suggestSubplot:  (pid, index, context) =>
    http.post(`/projects/${pid}/plot-extraction/subplot`, { index, ...context }, { timeout: 60000 }).then(r => r.data),
};

// ---------- Community Forum (iter-35 + iter55z scope split) ----------
export const forumApi = {
  topics:       () => http.get('/forum/topics').then(r => r.data),
  threads:      (slug) => http.get(`/forum/topics/${slug}/threads`).then(r => r.data),
  createThread: (topic_slug, title, body) =>
    http.post('/forum/threads', { topic_slug, title, body }).then(r => r.data),
  thread:       (tid) => http.get(`/forum/threads/${tid}`).then(r => r.data),
  reply:        (tid, body) => http.post(`/forum/threads/${tid}/posts`, { body }).then(r => r.data),
  editPost:     (pid, body) => http.patch(`/forum/posts/${pid}`, { body }).then(r => r.data),
  deletePost:   (pid) => http.delete(`/forum/posts/${pid}`).then(r => r.data),
  // iter55z — SSE stream URL (browser EventSource opens it directly so
  // the session cookie is sent with credentials).
  streamUrl:    (scope) => {
    const base = (process.env.REACT_APP_BACKEND_URL || '') + '/api/forum/stream';
    return scope ? `${base}?scope=${encodeURIComponent(scope)}` : base;
  },
};

export const forumAdminApi = {
  listThreads:  () => http.get('/admin/forum/threads').then(r => r.data),
  pin:          (tid) => http.post(`/admin/forum/threads/${tid}/pin`).then(r => r.data),
  lock:         (tid) => http.post(`/admin/forum/threads/${tid}/lock`).then(r => r.data),
  deleteThread: (tid) => http.delete(`/admin/forum/threads/${tid}`).then(r => r.data),
  deletePost:   (pid) => http.delete(`/admin/forum/posts/${pid}`).then(r => r.data),
  // iter55z — moderation bans.
  listBans:     () => http.get('/admin/forum/bans').then(r => r.data),
  banUser:      (user_id, reason) => http.post('/admin/forum/bans', { user_id, reason }).then(r => r.data),
  unbanUser:    (user_id) => http.delete(`/admin/forum/bans/${user_id}`).then(r => r.data),
  saveTopic:    (slug, body) => http.put(`/admin/forum/topics/${slug}`, body).then(r => r.data),
};

// iter55z-c — Tier impersonation. Lets an admin browse as basic/pro/premier
// so they can test feature gating without a separate account.
export const impersonateApi = {
  get:   () => http.get('/admin/impersonate-tier').then(r => r.data),
  set:   (tier) => http.post('/admin/impersonate-tier', { tier }).then(r => r.data),
  clear: () => http.post('/admin/impersonate-tier', { tier: null }).then(r => r.data),
};

// ---------- LLM cost summary (iter55z-d) — Admin sidebar card ----------
export const llmCostApi = {
  summary:         () => http.get('/admin/llm-cost/summary').then(r => r.data),
  setSpendAlert:   (v) => http.put('/admin/llm-cost/spend-alert', { spend_alert_usd: Number(v) }).then(r => r.data),
  setDowngrade:    (enabled, threshold, fallbackId) => http.put('/admin/llm-cost/model-downgrade', {
    enabled:             !!enabled,
    daily_threshold_usd: Number(threshold || 0),
    fallback_model_id:   String(fallbackId || ''),
  }).then(r => r.data),
};

// ---------- Help admin (iter-36) ----------
export const helpAdminApi = {
  get:       () => http.get('/admin/help/manual').then(r => r.data),
  saveMeta:  (meta) => http.put('/admin/help/manual/meta', meta).then(r => r.data),
  upsertSection: (id, section) =>
    http.put(`/admin/help/manual/sections/${id}`, section).then(r => r.data),
  deleteSection: (id) => http.delete(`/admin/help/manual/sections/${id}`).then(r => r.data),
  reorder:   (ids) => http.put('/admin/help/manual/order', { ids }).then(r => r.data),
  resetDefaults: () => http.post('/admin/help/manual/reset-defaults').then(r => r.data),
  // iter-40 — broadcast status + force-send
  broadcastStatus: () => http.get('/admin/help/manual/broadcast/status').then(r => r.data),
  broadcastNow:    () => http.post('/admin/help/manual/broadcast').then(r => r.data),
  // iter-2.40 — Drawer Tips CRUD
  getDrawerPanels:   () => http.get('/admin/help/drawer-panels').then(r => r.data),
  upsertDrawerPanel: (key, data) => http.put(`/admin/help/drawer-panels/${key}`, data).then(r => r.data),
  deleteDrawerPanel: (key) => http.delete(`/admin/help/drawer-panels/${key}`).then(r => r.data),
  resetDrawerPanels: () => http.post('/admin/help/drawer-panels/reset').then(r => r.data),
};

// ---------- Help manual (iter-35) ----------
export const helpApi = {
  manual:       () => http.get('/help/manual.json').then(r => r.data),
  pdfUrl:       () => `${API_BASE}/help/manual.pdf`,
  drawerPanels: () => http.get('/help/drawer-panels').then(r => r.data),
};

// ---------- Royalties (iter-34) ----------
// iter-ab1 — Generate Audiobook UI (whole-novel TTS narration, OpenAI or
// Fish Audio cloned voice). Backend: routes_audiobook.py.
export const audiobookApi = {
  voices:     () => http.get('/audiobook/voices').then(r => r.data),
  fishVoices: () => http.get('/audiobook/fish-voices').then(r => r.data),
  // Voice preview — synthesize a short sample for a Fish Audio cloned voice
  fishVoicePreviewUrl: (voiceDocId) => `${API_BASE}/audiobook/voice-preview/${encodeURIComponent(voiceDocId)}`,
  status:     (pid) => http.get(`/projects/${pid}/audiobook`).then(r => r.data),
  generate:   (pid, voice, provider) => http.post(`/projects/${pid}/audiobook`, { voice, provider }).then(r => r.data),
  chapterAudioUrl: (jobId, number) => `${API_BASE}/audiobook/chapter/${jobId}/${number}/audio`,
  // 2026-10 — Pay-to-unlock the rest of a sample-limited audiobook job.
  unlockCheckout: (jobId) => http.post(`/audiobook/${jobId}/unlock-checkout`, {
    origin_url: window.location.origin,
  }).then(r => r.data),
  // Per-production flat-fee billing
  productionPrices:   ()    => http.get('/audiobook/production-prices').then(r => r.data),
  productionCheckout: (pid, provider = 'openai') => http.post(`/projects/${pid}/audiobook/production-checkout`, {
    origin_url: window.location.origin,
    provider,
  }).then(r => r.data),
  productionStatus:   (pid) => http.get(`/projects/${pid}/audiobook/production-status`).then(r => r.data),
};

export const canvaApi = {
  coverPrice:           ()    => http.get('/canva/cover/purchase-price').then(r => r.data),
  coverPurchaseCheckout: ()   => http.post('/canva/cover/purchase-checkout', {
    origin_url: window.location.origin,
  }).then(r => r.data),
  adminDashboard: () => http.get('/admin/canva/dashboard').then(r => r.data),
};

export const bonusAddonsApi = {
  mine:    () => http.get('/bonus-addons/mine').then(r => r.data),
  checkout: (sku, originUrl = window.location.origin) =>
    http.post(`/bonus-addons/${sku}/checkout`, { origin_url: originUrl }).then(r => r.data),
};

export const royaltiesApi = {
  summary:  () => http.get('/royalties/summary').then(r => r.data),
  byBook:   () => http.get('/royalties/by-book').then(r => r.data),
  recent:   (limit = 50) => http.get('/royalties/recent', { params: { limit } }).then(r => r.data),
  monthly:  () => http.get('/royalties/monthly').then(r => r.data),
  // iter-53n++ pass 11 — template-specific breakdown pane.
  templatesBreakdown: () => http.get('/royalties/templates-breakdown').then(r => r.data),
  // iter55g34 — Bookstore direct-split payouts ledger (per-sale rows
  // with paid_direct / paid_manual / pending status, plus CSV export).
  payoutsLedger: (limit = 200) =>
    http.get('/authors/me/payouts/ledger', { params: { limit } }).then(r => r.data),
  payoutsLedgerCsvUrl: () => `${API_BASE}/authors/me/payouts/ledger.csv`,
};

export const royaltiesAdminApi = {
  pending: () => http.get('/admin/royalties/pending-payouts').then(r => r.data),
  payouts: () => http.get('/admin/royalties/payouts').then(r => r.data),
  markPaid: (author_user_id, note = '', idempotency_key = null) =>
    http.post('/admin/royalties/mark-paid', {
      author_user_id,
      note,
      // iter-53n++ pass 12c — the backend now requires an
      // idempotency_key so a retry can't produce duplicate payouts.
      // Callers may supply their own (recommended — reuse across
      // retries of the SAME logical action). Fallback mints a fresh
      // one; safe when the caller can't double-fire (e.g. button is
      // disabled during flight).
      idempotency_key: idempotency_key || (crypto.randomUUID
        ? crypto.randomUUID()
        : `mp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
    }).then(r => r.data),
};

// ---------- Editing & Proofreading workflow (iter-29) ----------
export const editingApi = {
  // Author
  tiers:        () => http.get('/editing/tiers').then(r => r.data),
  quote:        (pid) => http.get(`/editing/projects/${pid}/quote`).then(r => r.data),
  status:       (pid) => http.get(`/editing/projects/${pid}/status`).then(r => r.data),
  request:      (pid, author_notes = '') => http.post(`/editing/projects/${pid}/request`, { author_notes }).then(r => r.data),
  pay:          (rid) => http.post(`/editing/requests/${rid}/pay`).then(r => r.data),
  cancel:       (rid) => http.post(`/editing/requests/${rid}/cancel`).then(r => r.data),
  publish:      (pid, body) => http.post(`/editing/projects/${pid}/publish`, body).then(r => r.data),
  // Cover-art tiers
  coverTiers:           () => http.get('/editing/cover-tiers').then(r => r.data),
  coverCustomCheckout:  (pid, body) =>
    http.post(`/editing/projects/${pid}/cover-custom/checkout`, body).then(r => r.data),
  coverOrderStatus:     (pid) =>
    http.get(`/editing/projects/${pid}/cover-order-status`).then(r => r.data),
  // Editor (role=editor)
  myAssignments:    () => http.get('/editing/editor/assignments').then(r => r.data),
  manuscript:       (rid) => http.get(`/editing/editor/requests/${rid}/manuscript`).then(r => r.data),
  editorComplete:   (rid, editor_notes = '') => http.post(`/editing/editor/requests/${rid}/complete`, { editor_notes }).then(r => r.data),
};

// ---------- Editing admin (iter-29) ----------
export const editingAdminApi = {
  getTiers:    () => http.get('/admin/editing/tiers').then(r => r.data),
  saveTiers:   (tiers) => http.put('/admin/editing/tiers', { tiers }).then(r => r.data),
  listRequests:(status = null) =>
    http.get('/admin/editing/requests', { params: status ? { status } : {} }).then(r => r.data),
  listEditors: () => http.get('/admin/editing/editors').then(r => r.data),
  assign:      (rid, editor_user_id) =>
    http.post(`/admin/editing/requests/${rid}/assign`, { editor_user_id }).then(r => r.data),
  complete:    (rid, editor_notes = '') =>
    http.post(`/admin/editing/requests/${rid}/complete`, { editor_notes }).then(r => r.data),
  toggleEditorRole: (uid, is_editor) =>
    http.put(`/admin/users/${uid}/editor-role`, { is_editor }).then(r => r.data),
  // Cover-art tiers
  getCoverTiers:    () => http.get('/admin/editing/cover-tiers').then(r => r.data),
  saveCoverTiers:   (tiers) => http.put('/admin/editing/cover-tiers', { tiers }).then(r => r.data),
  // Language markups
  getLanguageMarkups:  () => http.get('/admin/bookstore/language-markups').then(r => r.data),
  saveLanguageMarkups: (languages) => http.put('/admin/bookstore/language-markups', { languages }).then(r => r.data),
};

// ---------- Admin ----------
export const adminApi = {
  login: (password, user) => http.post('/admin/login', { password, user }).then(r => {
    adminAuth.user = r.data.user;
    if (r.data.expires_in) adminAuth.setExpiry(r.data.expires_in);
    return r.data;
  }),
  logout: () => http.post('/admin/logout').then(r => { adminAuth.clear(); return r.data; })
                   .catch(() => { adminAuth.clear(); return { ok: true }; }),
  // iter55g47 — Emailed password reset (public — no auth cookie required).
  requestPasswordReset: (adminUrl) =>
    http.post('/admin/password-reset/request', { admin_url: adminUrl || undefined }).then(r => r.data),
  confirmPasswordReset: (token, newPassword) =>
    http.post('/admin/password-reset/confirm', { token, new_password: newPassword }).then(r => r.data),
  verify: () => http.get('/admin/verify').then(r => {
    adminAuth.user = r.data.user || adminAuth.user;
    return r.data;
  }),
  // iter-32 — session hardening
  heartbeat: () => http.post('/admin/heartbeat').then(r => r.data),
  // Voluntarily extend the hard-expiry ceiling by 12 hours.
  refreshSession: () => http.post('/admin/refresh').then(r => {
    if (r.data?.ok) {
      // Re-anchor the client-side expiry counter from the server response.
      const hardIso = r.data.hard_expires_at;
      if (hardIso) {
        const epochMs = new Date(hardIso).getTime();
        if (epochMs > 0) localStorage.setItem('pw-admin-expiry', String(epochMs));
      }
    }
    return r.data;
  }),
  me:        () => http.get('/admin/me').then(r => r.data),
  changeMyPassword: (current_password, new_password) =>
    http.post('/admin/me/change-password', { current_password, new_password }).then(r => r.data),
  // Oct-2026 · self-service recovery/notification email (Admin → My Profile)
  setMyEmail: (email) => http.put('/admin/me/email', { email }).then(r => r.data),
  // 2026-10 — Admin Loom bridge: mints a writer session for the fixed
  // "Admin Troubleshooting" sandbox account so the admin can click
  // straight into /app. Requires the admin_session cookie (401s
  // otherwise, same `require_admin` dependency as every other admin route).
  // 2026-08 — Optional project_id adds the sandbox account as an editor
  // collaborator on that manuscript (require_editor_access: admins + editor scope).
  loomBridge: (projectId) => http.post('/auth/admin-loom-bridge', { project_id: projectId || undefined }).then(r => r.data),
  inviteEditor: (body) => http.post('/admin/invite-editor', body).then(r => r.data),
  loomManuscripts: (q) => http.get('/admin/loom-manuscripts', { params: { q: q || undefined } }).then(r => r.data),
  // Feb-2026 — AI-generated, not-yet-published bookstore titles (filtered
  // sibling to loomManuscripts above), for the Admin Troubleshooting bar.
  aiDraftNovels: () => http.get('/admin/bookstore/ai-draft-novels').then(r => r.data),
  // 2026-10 — Mobile Staff biometric lock (gate is public/no-auth;
  // My Profile management calls go through the normal admin_session cookie).
  mobileLockStatus:        () => http.get('/admin/mobile-lock/status').then(r => r.data),
  mobileLockGateCheck:     () => http.get('/admin/mobile-lock/gate/check').then(r => r.data),
  mobileLockGateWebauthnOptions: () => http.post('/admin/mobile-lock/gate/webauthn/options').then(r => r.data),
  mobileLockGateWebauthnVerify:  (credential) =>
    http.post('/admin/mobile-lock/gate/webauthn/verify', { credential }).then(r => r.data),
  mobileLockGatePin:       (pin) => http.post('/admin/mobile-lock/gate/pin', { pin }).then(r => r.data),
  mobileLockMe:            () => http.get('/admin/mobile-lock/me').then(r => r.data),
  mobileLockToggle:        (enabled) => http.post('/admin/mobile-lock/toggle', { enabled }).then(r => r.data),
  mobileLockSetPin:        (pin) => http.post('/admin/mobile-lock/pin', { pin }).then(r => r.data),
  mobileLockClearPin:      () => http.delete('/admin/mobile-lock/pin').then(r => r.data),
  mobileLockRegisterOptions: () => http.post('/admin/mobile-lock/register/options').then(r => r.data),
  mobileLockRegisterVerify:  (credential, deviceLabel) =>
    http.post('/admin/mobile-lock/register/verify', { credential, device_label: deviceLabel }).then(r => r.data),
  mobileLockDeleteDevice:  (credentialId) =>
    http.delete(`/admin/mobile-lock/devices/${encodeURIComponent(credentialId)}`).then(r => r.data),
  mySessions:       () => http.get('/admin/me/sessions').then(r => r.data),
  revokeSession:    (token) => http.post('/admin/me/sessions/revoke', { token }).then(r => r.data),
  // iter-51z — admin user (account) management with per-user scopes
  adminUsersList:   () => http.get('/admin/admin-users').then(r => r.data),
  adminUsersCreate: (body) => http.post('/admin/admin-users', body).then(r => r.data),
  adminUsersSetScopes: (username, scopes) =>
    http.put(`/admin/admin-users/${encodeURIComponent(username)}/scopes`, { scopes }).then(r => r.data),
  adminUsersDelete: (username) =>
    http.delete(`/admin/admin-users/${encodeURIComponent(username)}`).then(r => r.data),
  adminUsersActivity: (limit = 20) =>
    http.get(`/admin/admin-users/activity?limit=${limit}`).then(r => r.data),
  // iter-admin-password-rotate — One-click "reset to fresh temp value" tool.
  adminUsersResetPassword: (username) =>
    http.post(`/admin/admin-users/${encodeURIComponent(username)}/reset-password`).then(r => r.data),
  // iter-click-fix — direct, visible password set for any admin account.
  adminUsersSetPassword: (username, newPassword) =>
    http.post(`/admin/admin-users/${encodeURIComponent(username)}/set-password`, { new_password: newPassword }).then(r => r.data),
  // Feb-2026 — Manual password set for regular users (support ops).
  setUserPassword: (userId, newPassword, reason = '') =>
    http.post(`/admin/users/${userId}/set-password`, { new_password: newPassword, reason }).then(r => r.data),
  // iter-companion-ping — Green/red pill probe.
  companionPing: () =>
    http.get('/admin/companion-integration/ping').then(r => r.data),
  // iter-companion-secret — Reveal + rotate the reverse-ping shared secret.
  companionSecretGet:    () => http.get('/admin/companion-integration/secret').then(r => r.data),
  companionSecretRotate: () => http.post('/admin/companion-integration/secret/rotate').then(r => r.data),
  companionKeysRotateAll:() => http.post('/admin/companion-integration/keys/rotate-all').then(r => r.data),
  companionSlackTestFire:() => http.post('/admin/companion-integration/slack-test-fire').then(r => r.data),
  companionRotationHistory: (params) => http.get('/admin/companion-integration/rotation-history', { params: params || {} }).then(r => r.data),
  companionRotationHistoryCsvUrl: (params) => {
    const p = new URLSearchParams(params || {}).toString();
    return `${API_URL}/admin/companion-integration/rotation-history.csv` + (p ? `?${p}` : '');
  },
  companionPingUrlSet:   (url) => http.put('/admin/companion-integration/ping-url', { url }).then(r => r.data),
  companionPingUrlClear: () => http.delete('/admin/companion-integration/ping-url').then(r => r.data),
  companionHeartbeats:   (hours = 24) => http.get('/admin/companion-integration/heartbeats', { params: { hours } }).then(r => r.data),
  // Sep-2026 · Dynamic LLM model catalog — DB-backed admin edits + freshness check
  updateModel:          (id, patch) => http.patch(`/admin/models/${id}`, patch).then(r => r.data),
  addModel:             (body) => http.post('/admin/models', body).then(r => r.data),
  markModelsReviewed:   () => http.post('/admin/models/mark-reviewed').then(r => r.data),
  runModelCatalogCheck: () => http.post('/admin/models/review-check/run-now').then(r => r.data),
  // Feb-2026 · Admin Opus edit-pass review
  opusReviewStart:       (pid) => http.post(`/admin/opus-review/projects/${pid}/start`).then(r => r.data),
  opusReviewJob:         (jobId) => http.get(`/admin/opus-review/jobs/${jobId}`).then(r => r.data),
  opusReviewLatest:      (pid) => http.get(`/admin/opus-review/projects/${pid}/latest`).then(r => r.data),
  opusReviewBulkResolve: (pid, body) => http.post(`/admin/opus-review/projects/${pid}/bulk-resolve`, body).then(r => r.data),
  // Feb-2026 · Author Publishing Agreement — admin surface
  authorContractsList:    (params) => http.get('/admin/author-contracts', { params: params || {} }).then(r => r.data),
  authorContractGet:      (cid) => http.get(`/admin/author-contracts/${cid}`).then(r => r.data),
  authorContractApprove:  (cid, body) => http.post(`/admin/author-contracts/${cid}/approve`, body).then(r => r.data),
  authorContractReject:   (cid, body) => http.post(`/admin/author-contracts/${cid}/reject`, body).then(r => r.data),
  authorContractPdfUrl:   (cid) => `${API_URL}/admin/author-contracts/${cid}/pdf`,
  // Feb-2026 · Author Publishing Agreement (platform-wide 85/15 · 70/30)
  publishingAgreementList:    (params) => http.get('/admin/author-agreements', { params: params || {} }).then(r => r.data),
  publishingAgreementGet:     (cid) => http.get(`/admin/author-agreements/${cid}`).then(r => r.data),
  publishingAgreementApprove: (cid, body) => http.post(`/admin/author-agreements/${cid}/approve`, body).then(r => r.data),
  publishingAgreementReject:  (cid, body) => http.post(`/admin/author-agreements/${cid}/reject`, body).then(r => r.data),
  publishingAgreementPdfUrl:  (cid) => `${API_URL}/admin/author-agreements/${cid}/pdf`,
  // Feb-2026 · ISBN Bank
  isbnAssignmentBankList:        (params) => http.get('/admin/isbn-bank', { params: params || {} }).then(r => r.data),
  isbnAssignmentBankGet:         (isbn) => http.get(`/admin/isbn-bank/${encodeURIComponent(isbn)}`).then(r => r.data),
  isbnAssignmentBankBulkImport:  (body) => http.post('/admin/isbn-bank/bulk-import', body).then(r => r.data),
  isbnAssignmentBankAssign:      (isbn, body) => http.post(`/admin/isbn-bank/${encodeURIComponent(isbn)}/assign`, body).then(r => r.data),
  isbnAssignmentBankUnassign:    (isbn, body) => http.post(`/admin/isbn-bank/${encodeURIComponent(isbn)}/unassign`, body || {}).then(r => r.data),
  isbnAssignmentBankPatch:       (isbn, body) => http.patch(`/admin/isbn-bank/${encodeURIComponent(isbn)}`, body).then(r => r.data),
  // iter-palette-named + iter-palette-history
  palettesList:      () => http.get('/admin/palettes').then(r => r.data),
  palettesSaveNamed: (body) => http.post('/admin/palettes/named', body).then(r => r.data),
  // iter-tips-editor — CRUD for HelpTip popover content served by /help/tips.
  tips: {
    list:   () => http.get('/admin/tips').then(r => r.data),
    create: (body) => http.post('/admin/tips', body).then(r => r.data),
    update: (slug, body) => http.patch(`/admin/tips/${encodeURIComponent(slug)}`, body).then(r => r.data),
    remove: (slug) => http.delete(`/admin/tips/${encodeURIComponent(slug)}`).then(r => r.data),
  },
  // Feb-2026 · Revenue-lifts feature registry (slow-release toggles).
  revenueFeatures: {
    list:   () => http.get('/admin/revenue-features').then(r => r.data),
    toggle: (key, body) => http.patch(`/admin/revenue-features/${encodeURIComponent(key)}`, body).then(r => r.data),
  },
  // iter-reader-sync-health — Companion ingest audit rollup for the sparkline.
  readerSyncHealth: (days = 14) => http.get('/admin/companion/ingest-health', { params: { days } }).then(r => r.data),
  // iter-companion-cert-pin — Admin controls for the Reader universal-link fingerprint pin.
  readerCert: {
    status: () => http.get('/admin/companion/reader-cert-status').then(r => r.data),
    pin:    () => http.post('/admin/companion/pin-reader-fingerprints').then(r => r.data),
    diff:   () => http.get('/admin/companion/reader-cert-diff').then(r => r.data),
    slackTestPing: () => http.post('/admin/companion/slack-test-ping', {}).then(r => r.data),
  },

  // iter-slack-webhook-config — DB override for the ops Slack incoming-webhook URL.
  slackWebhook: {
    get:    () => http.get('/admin/companion/slack-webhook').then(r => r.data),
    set:    (url) => http.post('/admin/companion/slack-webhook', { url }).then(r => r.data),
    clear:  () => http.delete('/admin/companion/slack-webhook').then(r => r.data),
  },

  // iter-ai-sync — Push The Loom's bookstore content to the Reader.
  // `mode` on books/all: "ai" | "regular" | "all". Books use `ai_assisted`
  // to route into the child app's AI-Assisted stack vs the main library.
  // iter-auto-mirror + iter-headline-composer add health + draft CRUD.
  aiSync: {
    preview:   () => http.get('/admin/ai-sync/preview').then(r => r.data),
    genres:    () => http.post('/admin/ai-sync/genres', {}).then(r => r.data),
    books:     (mode = 'ai') => http.post('/admin/ai-sync/books', {},
                                            { params: { mode } }).then(r => r.data),
    headlines: () => http.post('/admin/ai-sync/headlines', {}).then(r => r.data),
    all:       (mode = 'all') => http.post('/admin/ai-sync/all', {},
                                              { params: { mode } }).then(r => r.data),
    health:    () => http.get('/admin/ai-sync/health').then(r => r.data),
    listDrafts:   () => http.get('/admin/ai-sync/headline-drafts').then(r => r.data),
    saveDraft:    (payload) => http.post('/admin/ai-sync/headline-drafts', payload).then(r => r.data),
    deleteDraft:  (sourceId) => http.delete(`/admin/ai-sync/headline-drafts/${encodeURIComponent(sourceId)}`).then(r => r.data),
  },
  // iter-stripe-audit — Kill-switch flip history (last 5 by default).
  paymentsToggleAudit: (limit = 5) =>
    http.get('/admin/payments/toggle-audit', { params: { limit } }).then(r => r.data),

  // iter-inbox-router-rules — Runtime-editable role-mailbox → scope map.
  routerRules: {
    list:   () => http.get('/admin/scope-inbox/router-rules').then(r => r.data),
    upsert: (body) => http.post('/admin/scope-inbox/router-rules', body).then(r => r.data),
    remove: (local) => http.delete(`/admin/scope-inbox/router-rules/${encodeURIComponent(local)}`).then(r => r.data),
  },
  // iter-panel-inbox — Scope-tagged shared mailbox per admin panel.
  panelInbox: {
    list:      (scope, panel) => http.get('/admin/panel-inbox', { params: { scope, panel } }).then(r => r.data),
    markRead:  (id) => http.post(`/admin/panel-inbox/${encodeURIComponent(id)}/mark-read`).then(r => r.data),
    reply:     (id, body_md) => http.post(`/admin/panel-inbox/${encodeURIComponent(id)}/reply`, { body_md }).then(r => r.data),
    seedDemo:  () => http.post('/admin/panel-inbox/seed-demo').then(r => r.data),
    previewDigest: () => http.post('/admin/panel-inbox/preview-digest').then(r => r.data),
  },
  palettesDeleteNamed: (id) => http.delete(`/admin/palettes/named/${encodeURIComponent(id)}`).then(r => r.data),
  // iter-downtime-analytics — Aggregated shopper-clicks-while-off log.
  downtimeAnalytics: (limit = 200) =>
    http.get(`/admin/payments/downtime-analytics?limit=${limit}`).then(r => r.data),
  // iter-p2-r — QA training-user (testy@crownnrose.com) enable/disable
  qaUserStatus: () => http.get('/admin/qa-user/status').then(r => r.data),
  qaUserToggle: (enabled) =>
    http.post('/admin/qa-user/toggle', { enabled }).then(r => r.data),
  collections: () => http.get('/admin/collections').then(r => r.data),
  browse: (name, { limit = 100, skip = 0 } = {}) =>
    http.get(`/admin/collections/${name}?limit=${limit}&skip=${skip}`)
        .then(r => r.data),
  getDoc:    (name, id) => http.get(`/admin/collections/${name}/${id}`).then(r => r.data),
  patchDoc:  (name, id, patch) => http.put(`/admin/collections/${name}/${id}`, { patch }).then(r => r.data),
  deleteDoc: (name, id) => http.delete(`/admin/collections/${name}/${id}`).then(r => r.data),
  // Prompt caching admin
  getAiCaching:    ()          => http.get('/admin/ai-caching').then(r => r.data),
  updateAiCaching: (body)      => http.put('/admin/ai-caching', body).then(r => r.data),
  // Consolidated admin business endpoints
  users:     (params = {}) => http.get('/admin/users', { params }).then(r => r.data),
  exportCsvBlob: (params = {}) => http.get('/admin/users/export-csv', { params, responseType: 'blob' }).then(r => r.data),
  // Admin bulk manuscript export
  manuscriptExportPreview:  (userId) => http.get(`/admin/manuscript-export/${userId}/preview`).then(r => r.data),
  manuscriptExportDownload: (userId) => http.get(`/admin/manuscript-export/${userId}/download`, { responseType: 'blob' }).then(r => r),
  // Admin bulk manuscript import
  bulkManuscriptImportUsers: (q = '') => http.get('/admin/bulk-manuscript-import/users', { params: { q, limit: 100 } }).then(r => r.data),
  bulkManuscriptImport: (formData) => http.post('/admin/bulk-manuscript-import', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then(r => r.data),  updateUserEmails: (userId, body) => http.put(`/admin/users/${userId}/emails`, body).then(r => r.data),
  credit:    (userId, tokens, note='') => http.post(`/admin/users/${userId}/credit`, { tokens, note }).then(r => r.data),
  // iter55l — archive-based customer user deletion.
  archiveUser:            (userId, reason='') =>
    http.delete(`/admin/users/${userId}`, { data: { reason } }).then(r => r.data),
  archivedUsers:          () => http.get('/admin/users/archived').then(r => r.data),
  restoreArchivedUser:    (userId) =>
    http.post(`/admin/users/archived/${userId}/restore`).then(r => r.data),
  purgeArchivedUser:      (userId) =>
    http.delete(`/admin/users/archived/${userId}`).then(r => r.data),
  purgeManyArchivedUsers: (userIds) =>
    http.post('/admin/users/archived/purge-many', { user_ids: userIds }).then(r => r.data),
  // iter55f85 — Batch archive/delete for any live user.
  batchArchiveUsers:     (userIds, reason = '') =>
    http.post('/admin/users/batch-archive', { user_ids: userIds, reason }).then(r => r.data),
  batchDeleteUsers:      (userIds, reason = '') =>
    http.post('/admin/users/batch-delete',  { user_ids: userIds, reason }).then(r => r.data),
  // iter55f85 — Analytics dashboards.
  analyticsTools:        (days = 30, limit = 15) =>
    http.get('/admin/analytics/tools',       { params: { days, limit } }).then(r => r.data),
  analyticsPanels:       (days = 30) =>
    http.get('/admin/analytics/panels',      { params: { days } }).then(r => r.data),
  analyticsModels:       (days = 30) =>
    http.get('/admin/analytics/models',      { params: { days } }).then(r => r.data),
  analyticsHelp:         (days = 30) =>
    http.get('/admin/analytics/help',        { params: { days } }).then(r => r.data),
  analyticsConversions:  (days = 30) =>
    http.get('/admin/analytics/conversions', { params: { days } }).then(r => r.data),
  analyticsLanding:      (days = 30) =>
    http.get('/admin/analytics/landing',     { params: { days } }).then(r => r.data),
  // iter55o — retention config + manual trigger.
  getArchiveRetention:   () => http.get('/admin/users/archived/retention').then(r => r.data),
  setArchiveRetention:   (body) => http.put('/admin/users/archived/retention', body).then(r => r.data),
  runCleanupNow:         (dry_run = false) =>
    http.post('/admin/users/archived/run-cleanup-now', null, { params: { dry_run } }).then(r => r.data),
  // iter55m — manual subscription tier grant/clear (bypasses Stripe checkout).
  setSubscription:        (userId, body) =>
    http.post(`/admin/users/${userId}/subscription`, body).then(r => r.data),
  // iter55n — Bulk tier grant / clear (same tier applied to N users).
  grantManySubscriptions: (userIds, body) =>
    http.post('/admin/users/grant-many', { user_ids: userIds, ...body }).then(r => r.data),
  // iter-16 — Copyscape token wallet (separate from the AI token wallet).
  copyscapeGrant: (userId, tokens, note='') => http.post(`/admin/users/${userId}/copyscape-grant`, { tokens, note }).then(r => r.data),
  copyscapeBalance: () => http.get('/admin/copyscape/balance').then(r => r.data),
  // iter-17 — user type (regular vs special).
  setUserType: (userId, user_type) => http.post(`/admin/users/${userId}/user-type`, { user_type }).then(r => r.data),
  setTrainingAccreditation: (userId, accredited) => http.put(`/admin/users/${userId}/training-accreditation`, { accredited }).then(r => r.data),
  setLoomLibraryAccess: (userId, enabled) => http.put(`/admin/users/${userId}/loom-library-access`, { enabled }).then(r => r.data),
  // iter-28 — admin-triggered password reset email for email-login users.
  sendResetLink: (userId) => http.post(`/admin/users/${userId}/send-reset-link`).then(r => r.data),
  setRole:   (userId, role) => http.put(`/admin/users/${userId}/role`, { role }).then(r => r.data),
  config:    () => http.get('/admin/config').then(r => r.data),
  saveConfig:(patch) => http.put('/admin/config', patch).then(r => r.data),
  // Request log (build / feature request history export)
  saveRequestLog:      (body) => http.post('/admin/request-log', body).then(r => r.data),
  listRequestLogs:     () => http.get('/admin/request-log').then(r => r.data),
  triggerLogSnapshot:  () => http.post('/admin/request-log/snapshot').then(r => r.data),
  // Feb-2026 — Companion integration cross-app admin.
  companionIntegrationStatus: ()      => http.get('/admin/companion-integration').then(r => r.data),
  companionRotateServiceKey:   ()      => http.post('/admin/companion-integration/rotate-service-key').then(r => r.data),
  companionRotateJwtSecret:    ()      => http.post('/admin/companion-integration/rotate-jwt-secret').then(r => r.data),
  companionSetServiceKey:      (value) => http.put('/admin/companion-integration/service-key', { value }).then(r => r.data),
  companionSetJwtSecret:       (value) => http.put('/admin/companion-integration/jwt-secret',   { value }).then(r => r.data),
  companionClearServiceKey:    ()      => http.delete('/admin/companion-integration/service-key').then(r => r.data),
  companionClearJwtSecret:     ()      => http.delete('/admin/companion-integration/jwt-secret').then(r => r.data),
  companionFireTestEvent:      (event, payload = {}) =>
    http.post('/admin/companion-integration/fire-test-event', { event, payload }).then(r => r.data),
  companionPing:               ()      => http.post('/admin/companion-integration/ping-companion').then(r => r.data),
  // Feb-2026 — Outbound → Companion App creds (base URL + API key we use to call INTO the Reader).
  companionSetAppUrl:          (value) => http.put('/admin/companion-integration/companion-app-url',     { value }).then(r => r.data),
  companionSetAppApiKey:       (value) => http.put('/admin/companion-integration/companion-app-api-key', { value }).then(r => r.data),
  companionClearAppUrl:        ()      => http.delete('/admin/companion-integration/companion-app-url').then(r => r.data),
  companionClearAppApiKey:     ()      => http.delete('/admin/companion-integration/companion-app-api-key').then(r => r.data),
  companionAppDiagnostics:     ()      => http.post('/admin/companion-integration/companion-app-diagnostics').then(r => r.data),
  companionAppLastDiag:        ()      => http.get('/admin/companion-integration/companion-app-last-diagnostic').then(r => r.data),
  // iter55m/n — Beat-map JSON import + auto-detect + import history + Email provider health.
  importBeatMap: (body) => http.post('/admin/starter-templates/import', body).then(r => r.data),
  detectPlaceholders: (beat_map) =>
    http.post('/admin/starter-templates/detect-placeholders', { beat_map }).then(r => r.data),
  importHistory: (limit = 20) =>
    http.get(`/admin/starter-templates/import-history?limit=${limit}`).then(r => r.data),
  genreVariantsGet: () => http.get('/admin/genre-variants').then(r => r.data),
  genreVariantsPut: (variants) =>
    http.put('/admin/genre-variants', { variants }).then(r => r.data),
  emailProviderHealth: () => http.get('/admin/email-provider-health').then(r => r.data),
  aliasesPurgeable:    () => http.get('/admin/aliases/purgeable').then(r => r.data),
  aliasesPurge:        (uid) => http.post(`/admin/aliases/${uid}/purge`).then(r => r.data),
  aliasesSweepNow:     () => http.post('/admin/aliases/sweep-now').then(r => r.data),
  aliasesUnsuspend:    (uid) => http.post(`/admin/aliases/${uid}/unsuspend`).then(r => r.data),
  overageList:         (limit = 200) => http.get('/admin/billing/overage', { params: { limit } }).then(r => r.data),
  overageWaive:        (id, reason) => http.post(`/admin/billing/overage/${id}/waive`, { reason }).then(r => r.data),
  overageGrantExtra:   (uid, units = 50, reason = '', ttl_hours = 24) =>
    http.post(`/admin/users/${uid}/overage-grant`, { units, reason, ttl_hours }).then(r => r.data),
  overageGrantSuggest: (uid) => http.get(`/admin/users/${uid}/overage-grant-suggest`).then(r => r.data),
  userAudit:           (uid, kind = null, limit = 50) => http.get(`/admin/users/${uid}/audit`, { params: { kind, limit } }).then(r => r.data),
  emailSendLog:         (params = {}) => http.get('/admin/email-send-log', { params }).then(r => r.data),
  emailPurgeFailed:     () => http.delete('/admin/email-send-log', { params: { status: 'failed' } }).then(r => r.data),
  emailQuotaStats:      (hours = 24) => http.get('/admin/email/quota-stats', { params: { hours } }).then(r => r.data),
  emailSubdomainDns:    () => http.get('/admin/email/send-subdomain-dns').then(r => r.data),
  budget:    () => http.get('/admin/budget').then(r => r.data),
  // Roles & permissions
  roles:     () => http.get('/admin/roles').then(r => r.data),
  saveRoles: (grid) => http.put('/admin/roles', { grid }).then(r => r.data),
  rolePresets: () => http.get('/admin/roles/presets').then(r => r.data),
  applyRolePreset: (presetId) => http.post(`/admin/roles/presets/${presetId}`).then(r => r.data),
  permissionPreview: (userId, projectId) =>
    http.get(`/admin/permission-preview`, { params: { user_id: userId, project_id: projectId || undefined } }).then(r => r.data),
  // Activity / audit feed
  audit:     ({ limit = 100, skip = 0 } = {}) =>
    http.get(`/admin/audit`, { params: { limit, skip } }).then(r => r.data),
  // Admin token pool
  pool:      () => http.get('/admin/pool').then(r => r.data),
  acquirePool: (tokens, note='') => http.post('/admin/pool/acquire', { tokens, note }).then(r => r.data),
  // Project membership (admin convenience)
  projectMembers: (pid) => http.get(`/admin/projects/${pid}/members`).then(r => r.data),
  addProjectMember: (pid, user_id, role) => http.post(`/admin/projects/${pid}/members`, { user_id, role }).then(r => r.data),
  removeProjectMember: (pid, user_id) => http.delete(`/admin/projects/${pid}/members/${user_id}`).then(r => r.data),
  // SEO & robots (iter-12)
  getRobots:    () => http.get('/admin/robots').then(r => r.data),
  saveRobots:   (patch) => http.put('/admin/robots', patch).then(r => r.data),
  listSeo:      () => http.get('/admin/seo').then(r => r.data),
  saveSeoRoute: (route, patch) => http.put(`/admin/seo${route.startsWith('/') ? route : '/' + route}`, patch).then(r => r.data),
  deleteSeoRoute: (route) => http.delete(`/admin/seo${route.startsWith('/') ? route : '/' + route}`).then(r => r.data),
  suggestSeo:   (route, page_summary, audience) =>
    http.post('/admin/seo-suggest',
              { route: route || '/', page_summary, audience }).then(r => r.data),
  // Blog CMS (2026-10)
  listBlogPosts:  () => http.get('/admin/blog').then(r => r.data),
  createBlogPost: (body) => http.post('/admin/blog', body).then(r => r.data),
  updateBlogPost: (id, patch) => http.put(`/admin/blog/${id}`, patch).then(r => r.data),
  deleteBlogPost: (id) => http.delete(`/admin/blog/${id}`).then(r => r.data),
  // Auto-generate blog posts with Claude Haiku (2026-02)
  autoGenerateBlogPost: (body) => http.post('/admin/blog/auto-generate', body).then(r => r.data),
  // Backfill utilities (2026-02)
  backfillBlogCovers: () => http.post('/admin/blog/backfill-covers', {}).then(r => r.data),
  backfillBlogLocaleLinks: () => http.post('/admin/blog/backfill-locale-links', {}).then(r => r.data),
  // Email sender test (iter-13)
  testEmail:    (to) => http.post('/admin/test-email', { to }).then(r => r.data),
  // Capacity & storage (iter-20)
  capacityReport:    ()        => http.get('/admin/capacity/report').then(r => r.data),
  capacitySnapshots: (limit=90) => http.get('/admin/capacity/snapshots', { params: { limit } }).then(r => r.data),
  saveCapacityThresholds: (patch) => http.put('/admin/capacity/thresholds', patch).then(r => r.data),
  triggerCapacityAlertCheck: () => http.post('/admin/capacity/alert/check').then(r => r.data),
  capacityAlerts:    (limit=50) => http.get('/admin/capacity/alerts', { params: { limit } }).then(r => r.data),
  // iter-53n++ pass 12 — vCPU / compute planner.
  runCapacityPlan: (body = {}) =>
    http.post('/admin/capacity/plan', body, { timeout: 45000 }).then(r => r.data),
  // iter-21 — subscription plan catalog editor
  listPlans:         () => http.get('/admin/subscriptions/plans').then(r => r.data),
  savePlans:         (plans) => http.put('/admin/subscriptions/plans', { plans }).then(r => r.data),
  resetPlans:            () => http.post('/admin/subscriptions/plans/reset').then(r => r.data),
  getFoundingPromoConfig: () => http.get('/admin/founding-promo/config').then(r => r.data),
  saveFoundingPromoConfig: (payload) => http.put('/admin/founding-promo/config', payload).then(r => r.data),
  // iter-21 — accounting dashboards
  accountingSummary: (params={}) => http.get('/admin/accounting/summary', { params }).then(r => r.data),
  cashFlow:          (months=6)  => http.get('/admin/accounting/cash-flow', { params: { months } }).then(r => r.data),
  accountsAudit:     ()          => http.get('/admin/accounting/audit').then(r => r.data),
  communityFund:     ()          => http.get('/admin/community-publishing-fund').then(r => r.data),
  communityFundCreateFormula: (body) => http.post('/admin/community-publishing-fund/formulas', body).then(r => r.data),
  communityFundRunAccrual: (period = null) => http.post('/admin/community-publishing-fund/accruals/run', period ? { period } : {}).then(r => r.data),
  communityFundAddSpending: (body) => http.post('/admin/community-publishing-fund/spending', body).then(r => r.data),
  accountingEvents:  (params={}) => http.get('/admin/accounting/events',  { params }).then(r => r.data),
  listOpex:          (params={}) => http.get('/admin/accounting/opex',    { params }).then(r => r.data),
  recordOpex:        (body)      => http.post('/admin/accounting/opex',   body).then(r => r.data),
  deleteOpex:        (id)        => http.delete(`/admin/accounting/opex/${id}`).then(r => r.data),
  // Feb-2026 — CFO staff-input ledger (cost + AR rows)
  ledgerList:        (params={}) => http.get('/admin/ledger/entries',       { params }).then(r => r.data),
  ledgerCreate:      (body)      => http.post('/admin/ledger/entries',      body).then(r => r.data),
  ledgerStatus:      (id, status)=> http.put(`/admin/ledger/entries/${id}/status`, { status }).then(r => r.data),
  ledgerVoid:        (id, reason)=> http.post(`/admin/ledger/entries/${id}/void`, { reason }).then(r => r.data),
  ledgerDelete:      (id)        => http.delete(`/admin/ledger/entries/${id}`).then(r => r.data),
  ledgerSummary:     (months=6)  => http.get('/admin/ledger/summary', { params: { months } }).then(r => r.data),
  // Sep-2026 — GAAP Phase 1 (Chart of Accounts + Period Locking + Trial Balance)
  gaapListCoa:       ()          => http.get('/admin/gaap/coa').then(r => r.data),
  gaapSeedCoa:       ()          => http.post('/admin/gaap/coa/seed').then(r => r.data),
  gaapCreateCoa:     (body)      => http.post('/admin/gaap/coa', body).then(r => r.data),
  gaapDeleteCoa:     (code)      => http.delete(`/admin/gaap/coa/${code}`).then(r => r.data),
  gaapListPeriods:   ()          => http.get('/admin/gaap/periods').then(r => r.data),
  gaapClosePeriod:   (period)    => http.post('/admin/gaap/periods/close',  { period }).then(r => r.data),
  gaapReopenPeriod:  (period)    => http.post('/admin/gaap/periods/reopen', { period }).then(r => r.data),
  gaapTrialBalance:  (period, source='auto') => http.get('/admin/gaap/trial-balance', { params: { ...(period ? { period } : {}), source } }).then(r => r.data),
  gaapAgedAr:        ()          => http.get('/admin/gaap/aged-ar').then(r => r.data),
  gaapAgedAp:        ()          => http.get('/admin/gaap/aged-ap').then(r => r.data),
  gaapJournalList:   (params={}) => http.get('/admin/gaap/journal', { params }).then(r => r.data),
  gaapCreateJe:      (body)      => http.post('/admin/gaap/journal', body).then(r => r.data),
  payablesDigestSend:            ()       => http.post('/admin/accounting/payables/digest/send').then(r => r.data),
  payablesDigestRecipients:      ()       => http.get('/admin/accounting/payables/digest/recipients').then(r => r.data),
  payablesDigestAddRecipient:    (body)   => http.post('/admin/accounting/payables/digest/recipients', body).then(r => r.data),
  payablesDigestRemoveRecipient: (id)     => http.delete(`/admin/accounting/payables/digest/recipients/${id}`).then(r => r.data),
  credentialsFull:               ()       => http.get('/admin/credentials/full').then(r => r.data),
  updateCredentialScopes:        (username, scopes) => http.put(`/admin/credentials/${username}/scopes`, { scopes }).then(r => r.data),
  updateCredentialEmail:         (username, email)  => http.put(`/admin/credentials/${username}/email`,  { email  }).then(r => r.data),
  gaapReconciliation: ()         => http.get('/admin/gaap/reconciliation').then(r => r.data),
  gaapReconciliationBackfill: (limit=500) => http.post('/admin/gaap/reconciliation/backfill', { limit }).then(r => r.data),
  itCronReports:     (limit=50)  => http.get('/admin/it/cron-reports', { params: { limit } }).then(r => r.data),
  gaapChaseOne:      (id)        => http.post(`/admin/gaap/ar/chase/${id}`).then(r => r.data),
  gaapChaseBulk:     (buckets)   => http.post('/admin/gaap/ar/chase-bulk',
                                        buckets ? { only_buckets: buckets } : {}).then(r => r.data),
  // iter-26 — Teams & Projects
  listTeams:         ()          => http.get('/admin/teams').then(r => r.data),
  createTeam:        (name)      => http.post('/admin/teams', { name }).then(r => r.data),
  updateTeam:        (id, patch) => http.put(`/admin/teams/${id}`, patch).then(r => r.data),
  deleteTeam:        (id)        => http.delete(`/admin/teams/${id}`).then(r => r.data),
  assignProject:     (tid, pid)  => http.post(`/admin/teams/${tid}/projects`, { project_id: pid }).then(r => r.data),
  unassignProject:   (tid, pid)  => http.delete(`/admin/teams/${tid}/projects/${pid}`).then(r => r.data),
  listMembers:       (tid)       => http.get(`/admin/teams/${tid}/members`).then(r => r.data),
  addMember:         (tid, user_id, role='member') =>
                                    http.post(`/admin/teams/${tid}/members`, { user_id, role }).then(r => r.data),
  updateMember:      (tid, mid, patch) =>
                                    http.put(`/admin/teams/${tid}/members/${mid}`, patch).then(r => r.data),
  removeMember:      (tid, mid)  => http.delete(`/admin/teams/${tid}/members/${mid}`).then(r => r.data),
  allocateTokens:    (tid, mid, tokens, note='') =>
                                    http.post(`/admin/teams/${tid}/members/${mid}/allocate-tokens`, { tokens, note }).then(r => r.data),
  createUser:        (body)      => http.post('/admin/users', body).then(r => r.data),
  editUserProfile:   (uid, patch)=> http.put(`/admin/users/${uid}/profile`, patch).then(r => r.data),
  listAllProjects:   ()          => http.get('/admin/projects').then(r => r.data),
  listAdminUsers:    ()          => http.get('/admin/admins').then(r => r.data),
  // iter-51 — ISBN administration
  listIsbnAll:       (status)    => http.get('/admin/isbn', { params: status ? { status } : {} }).then(r => r.data),
  // Sep-14-2026 — ISBN Bank + Bowker ONIX + Barcode + eCO Filing Prep
  isbnBankPaste:         (payload, imprint, pool)  => http.post('/admin/isbn/bank/paste', { payload, imprint, pool }).then(r => r.data),
  isbnBowkerBankList:    (status = 'available') => http.get('/admin/isbn/bank', { params: { status } }).then(r => r.data),
  isbnBankPools:         ()           => http.get('/admin/isbn/bank/pools').then(r => r.data),   // Feb-2026 · per-pool counts
  isbnAllocate:          (recordId, pool)   => http.post(`/admin/isbn/${recordId}/allocate`, null, { params: pool ? { pool } : {} }).then(r => r.data),
  isbnBowkerOnixExport:  ()           => http.post('/admin/isbn/bowker/onix-export', null, { responseType: 'blob' }).then(r => r),
  isbnEcoFilingPrep:     (recordId)   => http.get(`/admin/isbn/${recordId}/eco-filing-prep`).then(r => r.data),
  isbnBankThreshold:     ()           => http.get('/admin/isbn/bank/threshold').then(r => r.data),
  isbnBankWarningsGet:   ()           => http.get('/admin/isbn/bank/warnings-config').then(r => r.data),
  isbnBankWarningsSet:   (enabled)    => http.post('/admin/isbn/bank/warnings-config', { enabled }).then(r => r.data),
  isbnLedger:            ()           => http.get('/admin/isbn/ledger').then(r => r.data),
  isbnBowkerOptions:     ()           => http.get('/admin/isbn/bowker-options').then(r => r.data),
  // Feb-2026 · Vendor Invoices folder (CFO Panel)
  vendorInvoicesList:    (params)     => http.get('/admin/vendor-invoices', { params: params || {} }).then(r => r.data),
  vendorInvoiceUpload:   (formData)   => http.post('/admin/vendor-invoices', formData, {
                                              headers: { 'Content-Type': 'multipart/form-data' },
                                            }).then(r => r.data),
  vendorInvoicePatch:    (id, patch)  => http.put(`/admin/vendor-invoices/${id}`, patch).then(r => r.data),
  vendorInvoiceMarkPaid: (id)         => http.post(`/admin/vendor-invoices/${id}/mark-paid`).then(r => r.data),
  vendorInvoiceDelete:   (id)         => http.delete(`/admin/vendor-invoices/${id}`).then(r => r.data),
  vendorInvoiceFileUrl:  (id)         => `${API_URL}/admin/vendor-invoices/${id}/file`,
  listPayablesOpen:      ()           => http.get('/admin/accounting/payables', { params: { status: 'open' } }).then(r => r.data),
  isbnBarcodeUrl:        (id)         => `${API_URL}/admin/isbn/${id}/barcode.png`,
  isbnQrcodeUrl:         (id)         => `${API_URL}/admin/isbn/${id}/qrcode.png`,
  // iter-51s — Canva free-credit grant (admin gives N "free covers" to a user).
  canvaGrantFree:    (uid, count)=> http.post('/admin/canva/grant-free', { user_id: uid, count }).then(r => r.data),
  createIsbn:        (body)      => http.post('/admin/isbn', body).then(r => r.data),
  updateIsbn:        (id, body)  => http.put(`/admin/isbn/${id}`, body).then(r => r.data),
  issueIsbn:         (id, isbn)  => http.post(`/admin/isbn/${id}/issue`, { isbn }).then(r => r.data),
  discontinueIsbn:   (id)        => http.post(`/admin/isbn/${id}/discontinue`).then(r => r.data),
  removeIsbn:        (id)        => http.delete(`/admin/isbn/${id}`).then(r => r.data),
};

export const manuscriptLibraryApi = {
  list: () => http.get('/loom/manuscript-library').then((response) => response.data),
  load: (manuscriptId) => http.post(`/loom/manuscript-library/${encodeURIComponent(manuscriptId)}/load`).then((response) => response.data),
};

// iter-51 — author-facing ISBN API (signed in via session cookie)
export const isbnApi = {
  listMine:     ()         => http.get('/isbn/mine').then(r => r.data),
  request:      (body)     => http.post('/isbn/request', body).then(r => r.data),
  updateMine:   (id, body) => http.put(`/isbn/mine/${id}`, body).then(r => r.data),
  // Feb-2026 · Bowker wizard — JPG/PNG cover upload (base64 data URL, ≤2MB).
  uploadCover:  (id, body) => http.post(`/isbn/mine/${id}/cover`, body).then(r => r.data),
  deleteCover:  (id)       => http.delete(`/isbn/mine/${id}/cover`).then(r => r.data),
  // Feb-2026 · Author-facing ONIX 3.0 export (single record). Returns
  // the raw XML string so callers can save it or hand it to Bowker.
  onixUrl:      (id)       => `${API_BASE}/isbn/mine/${id}/onix.xml`,
};

// iter55i — Microsoft Clarity heat-map + Marketing panel.
export const clarityApi = {
  publicConfig: ()       => http.get('/clarity/config').then(r => r.data),
  adminGet:     ()       => http.get('/admin/clarity/config').then(r => r.data),
  adminPut:     (body)   => http.put('/admin/clarity/config', body).then(r => r.data),
  // Feb-2026 — proxied `project-live-insights` from Clarity's Data
  // Export API. `params` = { num_of_days, dimension1?, dimension2?, dimension3? }.
  liveInsights: (params = {}) => http
      .get('/admin/clarity/live-insights', { params })
      .then(r => r.data),
};

export const marketingApi = {
  storefrontInsights: (params = {}) => http.get('/authors/me/marketing/storefront-insights', { params }).then(r => r.data),
  opportunityBadge: (params = {}) => http.get('/authors/me/marketing/opportunity-badge', { params }).then(r => r.data),
  opportunities: (params = {}) => http.get('/authors/me/marketing/opportunities', { params }).then(r => r.data),
  socialListening: (params = {}) => http.get('/authors/me/marketing/social-listening', { params }).then(r => r.data),
  saveSocialPreferences: (body) => http.put('/authors/me/marketing/social-listening/preferences', body).then(r => r.data),
};

export const editorPublicationApi = {
  queue: (params = {}) => http.get('/editor/publications', { params }).then(r => r.data),
  detail: (id) => http.get(`/editor/publications/${id}`).then(r => r.data),
  updateGate: (id, gate, body) => http.put(`/editor/publications/${id}/gates/${gate}`, body).then(r => r.data),
  updateMetadata: (id, body) => http.put(`/editor/publications/${id}/metadata`, body).then(r => r.data),
  updateTaxonomy: (id, body) => http.put(`/editor/publications/${id}/taxonomy`, body).then(r => r.data),
  sync: (id, body) => http.post(`/editor/publications/${id}/sync`, body).then(r => r.data),
  assign: (id, body) => http.put(`/editor/publications/${id}/assignment`, body).then(r => r.data),
  workloads: () => http.get('/editor/assignments/workload').then(r => r.data),
  genreTree: () => http.get('/editor/genre-tree').then(r => r.data),
  createGenre: (body) => http.post('/editor/genre-tree', body).then(r => r.data),
  updateGenre: (slug, body) => http.put(`/editor/genre-tree/${slug}`, body).then(r => r.data),
  deleteGenre: (slug) => http.delete(`/editor/genre-tree/${slug}`).then(r => r.data),
};

// Feb-2026 — Sync-report + orphan-retire + spotlight rotator + author digest.
export const syncReportApi = {
  retired:      (limit = 50)      => http.get(`/admin/sync-report/retired?limit=${limit}`).then(r => r.data),
  republish:    (id)              => http.post(`/admin/sync-report/republish/${id}`).then(r => r.data),
  retireOrphans: (body)           => http.post('/admin/sync-report/retire-orphans', body).then(r => r.data),
};
export const spotlightsApi = {
  adminGet:     ()      => http.get('/admin/bookstore/spotlights').then(r => r.data),
  adminPut:     (body)  => http.put('/admin/bookstore/spotlights', body).then(r => r.data),
  rotateNow:    ()      => http.post('/admin/bookstore/spotlights/rotate-now').then(r => r.data),
  preview:      ()      => http.get('/admin/bookstore/spotlights/preview').then(r => r.data),
};
// Feb-2026 — Sitemap refresh + search-engine notification.
export const sitemapPingApi = {
  ping:    (body = {})   => http.post('/admin/seo/sitemap-ping', body).then(r => r.data),
  refresh: ()            => http.post('/admin/seo/sitemap/refresh').then(r => r.data),
  history: (limit = 10)  => http.get(`/admin/seo/sitemap-ping/history?limit=${limit}`).then(r => r.data),
};

// Feb-2026 — Reader push history + native-Anthropic route safety valve.
export const readerPushApi = {
  history:   (limit = 5)     => http.get(`/admin/ai-sync/history?limit=${limit}`).then(r => r.data),
  pushNow:   (mode = 'all')  => http.post(`/admin/ai-sync/all?mode=${mode}`).then(r => r.data),
};
export const routeValveApi = {
  status:  ()   => http.get('/admin/route-valve/status').then(r => r.data),
  reset:   ()   => http.post('/admin/route-valve/reset').then(r => r.data),
};

// Feb-2026 — Loom blurb generator (Claude Sonnet via Emergent LLM key).
export const loomBlurbApi = {
  generate: (mid, body = {}) => http.post(`/loom/manuscripts/${mid}/blurb`, body).then(r => r.data),
  bulk:     (body = {})      => http.post('/loom/manuscripts/blurbs/bulk-missing', body).then(r => r.data),
};

// iter55k — Phase 5: Premier Studio human services.
export const humanServicesApi = {
  quota:          ()             => http.get('/human-services/quota').then(r => r.data),
  coachingSlots:  ()             => http.get('/human-services/coaching/slots').then(r => r.data),
  coachingBook:   (slot_id, notes) =>
      http.post('/human-services/coaching/book', { slot_id, notes }).then(r => r.data),
  coachingMine:   ()             => http.get('/human-services/coaching/mine').then(r => r.data),
  coachingCancel: (sid)          => http.post(`/human-services/coaching/${sid}/cancel`).then(r => r.data),
  reviewRequest:  (project_id, notes) =>
      http.post('/human-services/editor-review/request', { project_id, notes }).then(r => r.data),
  reviewMine:     ()             => http.get('/human-services/editor-review/mine').then(r => r.data),
  reviewGet:      (rid)          => http.get(`/human-services/editor-review/${rid}`).then(r => r.data),
};

// iter55k — Admin-side human-services queue + inline comments.
export const humanServicesAdminApi = {
  listAvailability:  (include_taken = true) =>
      http.get('/admin/human-services/availability', { params: { include_taken } }).then(r => r.data),
  createSlot:        (body)      => http.post('/admin/human-services/availability', body).then(r => r.data),
  deleteSlot:        (id)        => http.delete(`/admin/human-services/availability/${id}`).then(r => r.data),
  listCoaching:      ()          => http.get('/admin/human-services/coaching').then(r => r.data),
  patchCoaching:     (id, body)  => http.put(`/admin/human-services/coaching/${id}`, body).then(r => r.data),
  listReviews:       ()          => http.get('/admin/human-services/editor-reviews').then(r => r.data),
  patchReview:       (id, body)  => http.put(`/admin/human-services/editor-reviews/${id}`, body).then(r => r.data),
  listComments:      (rid)       => http.get(`/admin/human-services/editor-reviews/${rid}/comments`).then(r => r.data),
  addComment:        (rid, body) => http.post(`/admin/human-services/editor-reviews/${rid}/comments`, body).then(r => r.data),
  editComment:       (cid, text) => http.put(`/admin/human-services/editor-reviews/comments/${cid}`, { text }).then(r => r.data),
  deleteComment:     (cid)       => http.delete(`/admin/human-services/editor-reviews/comments/${cid}`).then(r => r.data),
};

export const editorialOrdersAdminApi = {
  listOrders:       (status)       => http.get('/admin/editorial/orders', { params: status ? { status } : {} }).then(r => r.data),
  getOrder:         (id)           => http.get(`/admin/editorial/orders/${id}`).then(r => r.data),
  assignEditor:     (id, editor_id, admin_notes) => http.patch(`/admin/editorial/orders/${id}/assign`, { editor_id, admin_notes }).then(r => r.data),
  transitionStatus: (id, status, admin_notes)    => http.patch(`/admin/editorial/orders/${id}/status`, { status, admin_notes }).then(r => r.data),
  listPayouts:      ()             => http.get('/admin/editorial/payouts').then(r => r.data),
  listEditors:      ()             => http.get('/admin/editorial/editors').then(r => r.data),
};

export const editorialReviewApi = {
  list:       (params = {}) => http.get('/admin/editorial-review/queue', { params }).then(r => r.data),
  detail:     (id)          => http.get(`/admin/editorial-review/${id}`).then(r => r.data),
  editors:    ()            => http.get('/admin/editorial-review/editors').then(r => r.data),
  sync:       (body = {})   => http.post('/admin/editorial-review/sync', body).then(r => r.data),
  rescore:    (id)          => http.post(`/admin/editorial-review/${id}/rescore`).then(r => r.data),
  assign:     (id, editor)  => http.post(`/admin/editorial-review/${id}/assign`, { editor }).then(r => r.data),
  saveNotes:  (id, notes)   => http.put(`/admin/editorial-review/${id}/notes`, { notes }).then(r => r.data),
  decide:     (id, action, notes = '') => http.post(`/admin/editorial-review/${id}/decision`, { action, notes }).then(r => r.data),
};

export const mediaDevelopmentApi = {
  list:   () => http.get('/admin/media-development').then(r => r.data),
  slackAlerts: () => http.get('/admin/media-development/slack-alerts').then(r => r.data),
  updateSlackAlerts: (body) => http.put('/admin/media-development/slack-alerts', body).then(r => r.data),
  flushSlackDigest: () => http.post('/admin/media-development/slack-alerts/flush').then(r => r.data),
  upload: (formData, onUploadProgress) => http.post('/admin/media-development/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    timeout: 300000,
  }).then(r => r.data),
  update: (id, body) => http.patch(`/admin/media-development/${id}`, body).then(r => r.data),
  replaceThumbnail: (id, file) => {
    const formData = new FormData();
    formData.append('thumbnail', file);
    return http.post(`/admin/media-development/${id}/thumbnail`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then(r => r.data);
  },
  replacePoster: (id, file) => {
    const formData = new FormData();
    formData.append('poster', file);
    return http.post(`/admin/media-development/${id}/poster`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then(r => r.data);
  },
};

// ─── Onboarding Activation Wizard ────────────────────────────────────
export const onboardingApi = {
  getChecklist: (pid) => http.get(`/onboarding/checklist?project_id=${pid}`).then(r => r.data),
  dismiss:      (pid) => http.post('/onboarding/dismiss', { project_id: pid }).then(r => r.data),
  undismiss:    (pid) => http.delete(`/onboarding/dismiss?project_id=${pid}`).then(r => r.data),
  activationStats: () => http.get('/admin/onboarding/activation-stats').then(r => r.data),
};

// ─── Marketing Asset Generator + Social Scheduler + ARC Outreach ──────
export const marketingAssetsApi = {
  generate:       (pid)         => http.post(`/projects/${pid}/marketing-assets/generate`).then(r => r.data),
  get:            (pid)         => http.get(`/projects/${pid}/marketing-assets`).then(r => r.data),
  createSchedule: (pid, body)   => http.post(`/projects/${pid}/social-schedule`, body).then(r => r.data),
  getSchedule:    (pid)         => http.get(`/projects/${pid}/social-schedule`).then(r => r.data),
  // ARC Outreach Tracker
  listArcContacts:   (pid)             => http.get(`/projects/${pid}/arc-contacts`).then(r => r.data),
  addArcContact:     (pid, body)       => http.post(`/projects/${pid}/arc-contacts`, body).then(r => r.data),
  updateArcContact:  (pid, cid, body)  => http.put(`/projects/${pid}/arc-contacts/${cid}`, body).then(r => r.data),
  deleteArcContact:  (pid, cid)        => http.delete(`/projects/${pid}/arc-contacts/${cid}`).then(r => r.data),
};

// ─── Comparison Landing Pages ─────────────────────────────────────────
export const comparisonApi = {
  listPublic: ()     => http.get('/compare').then(r => r.data),
  getPage:    (slug) => http.get(`/compare/${slug}`).then(r => r.data),
  adminList:  ()     => http.get('/admin/comparison-pages').then(r => r.data),
  generate:   (body) => http.post('/admin/comparison-pages/generate', body).then(r => r.data),
  regenerate: (slug) => http.post(`/admin/comparison-pages/${slug}/regenerate`).then(r => r.data),
};

// ─── Referral admin stats ─────────────────────────────────────────────
export const referralAdminApi = {
  stats: () => http.get('/referrals/admin/stats').then(r => r.data),
};

// ─── Free tier management + Tier migration ────────────────────────────
export const freeTierAdminApi = {
  status:           () => http.get('/admin/free-tier/status').then(r => r.data),
  closeTier:        () => http.post('/admin/free-tier/close').then(r => r.data),
  migrationPreview: () => http.get('/admin/tiers/migrate/preview').then(r => r.data),
  runMigration:     () => http.post('/admin/tiers/migrate').then(r => r.data),
};

export default {
  projectsApi, chaptersApi, charactersApi, variablesApi,
  generateApi, chatApi, exportApi, settingsApi, adminApi,
  versionsApi, plagiarismApi,
  paymentsApi: authPaymentsApi,
};

// ---------- Admin Bulk Novel Evaluation & Rewrite ----------
export const bulkNovelEvalApi = {
  projects:        ()       => http.get('/admin/bulk-eval/projects').then(r => r.data),
  estimate:        (body)   => http.post('/admin/bulk-eval/estimate', body).then(r => r.data),
  start:           (body)   => http.post('/admin/bulk-eval/start', body).then(r => r.data),
  jobs:            (limit)  => http.get('/admin/bulk-eval/jobs', { params: { limit: limit || 20 } }).then(r => r.data),
  job:             (jobId)  => http.get(`/admin/bulk-eval/jobs/${jobId}`).then(r => r.data),
  cancel:          (jobId)  => http.post(`/admin/bulk-eval/jobs/${jobId}/cancel`).then(r => r.data),
  results:         (jobId)  => http.get(`/admin/bulk-eval/jobs/${jobId}/results`).then(r => r.data),
  accept:          (rid)    => http.post(`/admin/bulk-eval/results/${rid}/accept`).then(r => r.data),
  reject:          (rid)    => http.post(`/admin/bulk-eval/results/${rid}/reject`).then(r => r.data),
  requeue:         (jobId, projectId) => http.post(`/admin/bulk-eval/jobs/${jobId}/requeue/${projectId}`).then(r => r.data),
  acceptAllMarkup: (jobId)  => http.post(`/admin/bulk-eval/jobs/${jobId}/accept-all-markup`).then(r => r.data),
};

// ---------- Heat & Emotional Tension Adjustment ----------
export const heatTensionApi = {
  startAdjust: (pid, body)      => http.post(`/projects/${pid}/heat-tension/adjust`, body).then(r => r.data),
  pollJob:     (pid, jobId)     => http.get(`/projects/${pid}/heat-tension/jobs/${jobId}`).then(r => r.data),
  applyJob:    (pid, jobId)     => http.post(`/projects/${pid}/heat-tension/jobs/${jobId}/apply`).then(r => r.data),
};

// ---------- Creative Interaction Tracker ----------
export const creativeInteractionApi = {
  recordParagraphEdit: (pid, cid, body) =>
    http.post(`/projects/${pid}/chapters/${cid}/creative-interaction`, body).then(r => r.data),
  recordChapterEvent: (pid, body) =>
    http.post(`/projects/${pid}/creative-interaction/chapter-event`, body).then(r => r.data),
  list: (pid) =>
    http.get(`/projects/${pid}/creative-interactions`).then(r => r.data),
};

// ---------- Copyright Filing ----------
export const copyrightFilingApi = {
  prices:   ()              => http.get('/copyright-filing/prices').then(r => r.data),
  checkout: (pid, body)     => http.post(`/projects/${pid}/copyright-filing/checkout`, body).then(r => r.data),
  status:   (pid)           => http.get(`/projects/${pid}/copyright-filing/status`).then(r => r.data),
};

// ---------- Novel Factory — bulk synopsis-to-manuscript pipeline ----------
export const novelFactoryApi = {
  parseFile:  (formData)  => http.post('/novel-factory/parse', formData,
                               { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 })
                               .then(r => r.data),
  queue:      (body)      => http.post('/novel-factory/queue', body, { timeout: 30000 }).then(r => r.data),
  getRun:     (runId)     => http.get(`/novel-factory/runs/${runId}`).then(r => r.data),
  listRuns:   ()          => http.get('/novel-factory/runs').then(r => r.data),
  cancelRun:  (runId)     => http.post(`/novel-factory/runs/${runId}/cancel`).then(r => r.data),
};

// ---------- Admin Copyright Filing ----------
export const copyrightFilingAdminApi = {
  dashboard:          ()              => http.get('/admin/copyright-filing/dashboard').then(r => r.data),
  list:               (skip=0,lim=100)=> http.get(`/admin/copyright-filing/list?skip=${skip}&limit=${lim}`).then(r => r.data),
  recordRegistration: (id, body)      => http.post(`/admin/copyright-filing/${id}/register`, body).then(r => r.data),
};

// ---------- User Manuscript Bulk Tools (user-facing, non-admin) ----------
export const userManuscriptApi = {
  exportPreview:  ()         => http.get('/user/manuscripts/export/preview').then(r => r.data),
  exportDownload: ()         => http.get('/user/manuscripts/export/download', { responseType: 'blob' }).then(r => r),
  import:         (formData) => http.post('/user/manuscripts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then(r => r.data),
};

// ---------- AI Manuscript Metadata Extraction ----------
export const extractionApi = {
  trigger:          (pid)             => http.post(`/manuscripts/${pid}/extract`).then(r => r.data),
  getDraft:         (pid)             => http.get(`/manuscripts/${pid}/extraction-draft`).then(r => r.data),
  updateDraft:      (pid, data)       => http.patch(`/manuscripts/${pid}/extraction-draft`, data).then(r => r.data),
  apply:            (pid, sections)   => http.post(`/manuscripts/${pid}/extraction-draft/apply`, { sections }).then(r => r.data),
  listVersions:     (pid)             => http.get(`/manuscripts/${pid}/extraction-drafts/versions`).then(r => r.data),
  activateVersion:  (pid, draftId)    => http.post(`/manuscripts/${pid}/extraction-drafts/${draftId}/activate`).then(r => r.data),
  extractChapter:   (pid, chapterId)  => http.post(`/manuscripts/${pid}/chapters/${chapterId}/extract`).then(r => r.data),
};

// ---------- ZIP Import Pipeline ----------
export const zipImportApi = {
  start:  (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post('/bulk-novel/zip-import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }).then(r => r.data);
  },
  runs:   ()        => http.get('/bulk-novel/zip-runs').then(r => r.data),
  run:    (runId)   => http.get(`/bulk-novel/zip-runs/${runId}`).then(r => r.data),
  cancel: (runId)   => http.post(`/bulk-novel/zip-runs/${runId}/cancel`).then(r => r.data),
};

// ---------- Craft Standards — Deviation Inbox ----------
export const craftDeviationsApi = {
  list:    (pid)        => http.get(`/projects/${pid}/craft-deviations`).then(r => r.data),
  resolve: (pid, devId) => http.patch(`/projects/${pid}/craft-deviations/${devId}/resolve`).then(r => r.data),
  reopen:  (pid, devId) => http.delete(`/projects/${pid}/craft-deviations/${devId}/resolve`).then(r => r.data),
};

// ---------- Subplot Arc Tracker ----------
export const subplotArcApi = {
  get:        (pid)       => http.get(`/projects/${pid}/subplot-arc`).then(r => r.data),
  toggleBeat: (pid, body) => http.post(`/projects/${pid}/subplot-arc/beat`, body).then(r => r.data),
};


export const coverArtApi = {
  generate: (pid) => http.post(`/projects/${pid}/generate-cover`).then(r => r.data),
};

export const kdpExportApi = {
  suggestKeywords: (pid) => http.post(`/projects/${pid}/kdp-keywords`).then(r => r.data),
  download: (pid, keywords = []) => {
    const params = new URLSearchParams();
    keywords.forEach(kw => kw && params.append('kw', kw));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return http.get(`/projects/${pid}/kdp-export${qs}`, { responseType: 'blob' })
      .then(r => {
        const url  = URL.createObjectURL(r.data);
        const a    = document.createElement('a');
        a.href     = url;
        const cd   = r.headers?.['content-disposition'] || '';
        const m    = cd.match(/filename[^;=\n]*=([^;\n"]*)/);
        a.download = m ? m[1].trim().replace(/"/g, '') : 'kdp_export.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  },
};

export const publishabilityApi = {
  list: () => http.get('/exports/publishable').then(r => r.data),
  downloadBatch: () =>
    http.get('/exports/publishable/batch', { responseType: 'blob' }).then(r => {
      const url  = URL.createObjectURL(r.data);
      const a    = document.createElement('a');
      a.href     = url;
      const cd   = r.headers?.['content-disposition'] || '';
      const m    = cd.match(/filename[^;=\n]*=([^;\n"]*)/);
      a.download = m ? m[1].trim().replace(/"/g, '') : 'publishable_novels.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }),
};

export const seriesSyncApi = {
  toggle:    (cid, enabled)           => http.patch(`/characters/${cid}/series-sync`, { enabled }).then(r => r.data),
  push:      (cid, target_project_id) => http.post(`/characters/${cid}/series-sync/push`, { target_project_id }).then(r => r.data),
  bulkPush:  (pid, target_project_id) => http.post(`/projects/${pid}/characters/bulk-series-sync`, { target_project_id }).then(r => r.data),
};


// ── Bible Drafts + Change Cue ─────────────────────────────────────────────
export const bibleDraftsApi = {
  get:         (pid)                          => http.get(`/projects/${pid}/bible-drafts`).then(r => r.data),
  save:        (pid, field_key, draft_value, field_label) =>
                 http.put(`/projects/${pid}/bible-drafts`, { field_key, draft_value, field_label }).then(r => r.data),
  discard:     (pid, field_key)               => http.delete(`/projects/${pid}/bible-drafts/${field_key}`).then(r => r.data),
  discardAll:  (pid)                          => http.delete(`/projects/${pid}/bible-drafts`).then(r => r.data),
  apply:       (pid, field_key)               => http.post(`/projects/${pid}/bible-drafts/${field_key}/apply`).then(r => r.data),
  clearCue:    (pid)                          => http.post(`/projects/${pid}/bible-drafts/clear-cue`).then(r => r.data),
};

export const revisionApi = {
  getAccepted:  (pid, cid)        => http.get(`/projects/${pid}/chapters/${cid}/revision/accepted`).then(r => r.data),
  saveAccepted: (pid, cid, notes) => http.put(`/projects/${pid}/chapters/${cid}/revision/accepted`, { notes }).then(r => r.data),
  getSummary:   (pid)             => http.get(`/projects/${pid}/revision/accepted-summary`).then(r => r.data),
};

